// ============================================================
// MotorGroupContent.jsx
// EV Powertrain Simulator — 모터 제어 탭 콘텐츠
// ControlModal에서 분리된 그룹 컴포넌트
// ============================================================
import React from 'react';
import {
  LineChart, BarChart, AreaChart, RadarChart, ScatterChart,
  Line, Bar, Area, Radar, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
  ResponsiveContainer, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Cell, ComposedChart,
} from 'recharts';
import { PI, PF, ModalSectionTitle } from '../shared/helpers.jsx';
import { mono } from '../shared/theme.js';
import { ControlLoopTabContent } from '../tabs/ControlLoopTabContent.jsx';
import { DRIVE_MODE_GAIN_TABLE, getModeGainsForUI,
  computeCurrentLoopBode, computeCurrentLoopBode_dq, computeSpeedLoopBode } from '../../core/control/motor_control.js';
import { svpwmModulate, saturatedInductance } from '../../core/physics/motor.js';
import { useColLayout } from '../../contexts/ColLayoutContext.js';

export function MotorGroupContent({ params, up, subTab, setSubTab, group, setGroup, COLORS, lang, e,
  getModePIGains, upModeGain, resetModeToDefault, resetAllModesToDefault,
  selLutMode, setSelLutMode, selAwdMode, setSelAwdMode,
  selRegenMode, setSelRegenMode, regenView, setRegenView,
  piEditMode, setPiEditMode,
  hideY2_ctrl_kw, set_hideY2_ctrl_kw,
}) {
  const colLayout = useColLayout();
  const _gc = colLayout === 1 ? '1fr' : colLayout === 3 ? '1fr 1fr 1fr' : colLayout === 4 ? '1fr 1fr 1fr 1fr' : '1fr 1fr';
  return (
    <div>
          {/* ══════════════════════════════════════════════════════════
              [A16] Cascade PID 제어 구조 블럭도
              외부 4열: 드라이버 | 속도루프 | 전류루프 | MTPA/MTPV/FW
              내부 각 열: 3~4행 수직 스택
          ══════════════════════════════════════════════════════════ */}
          {group === "motor" && (() => {
            const C_DRV = "#22c55e";
            const C_SPD = COLORS.accent2;
            const C_CUR = COLORS.accent;
            const C_FW  = "#a855f7";
            const C_HW  = "#ec4899";
            const C_MTR = "#06b6d4";
            const C_NEU = "#64748b";

            const isDrv = ["torque_ref"].includes(subTab);
            const isSpd = ["speed"].includes(subTab);
            const isCur = ["current","pwm"].includes(subTab);
            const isFW  = ["fw_mtpa"].includes(subTab);

            // BW 분리비
            const wci   = (params.Kp_id||1.51)/(params.Ld||0.0003);
            const J_eq  = (params.J_motor||0.05)+params.m*params.r_wheel**2/params.gear_ratio**2;
            const wcs   = (params.Kp_spd||143.4)/J_eq;
            const sep   = wci/Math.max(wcs,1);
            const sepOk = sep >= 5;

            // 내부 셀 (각 열 안의 행 블럭)
            const cell = (icon, title, color, tabs) => {
              const active = tabs && tabs.includes(subTab);
              return (
                <div onClick={() => tabs&&tabs.length>0&&setSubTab(tabs[0])}
                  style={{display:"flex",alignItems:"center",gap:'6px',
                    padding:"6px 8px",borderRadius:'6px',
                    background:active?`${color}30`:`${color}15`,
                    border:`1.5px solid ${active?color:color+'50'}`,
                    boxShadow:active?`0 0 7px ${color}50`:"none",
                    cursor:tabs&&tabs.length>0?"pointer":"default",
                    transition:"all 0.15s",width:"100%"}}>
                  <span style={{fontSize:'14px',lineHeight:1,flexShrink:0}}>{icon}</span>
                  <span style={{fontSize:'9px',fontWeight:700,
                    color:active?color:COLORS.text,lineHeight:1.3}}>{title}</span>
                </div>
              );
            };

            // 열 컨테이너 (헤더 라벨 + 내부 행 스택)
            const col = (name, bw, color, active, children) => (
              <div style={{display:"flex",flexDirection:"column",gap:0,
                flex:1,minWidth:0,borderRadius:'8px',overflow:"hidden",
                border:`2px solid ${active?color:color+'35'}`,
                background:active?`${color}0c`:`${color}05`,
                transition:"all 0.2s"}}>
                {/* 열 헤더 */}
                <div style={{padding:"6px 8px",
                  background:active?`${color}22`:`${color}12`,
                  borderBottom:`1px solid ${color}30`,
                  display:"flex",flexDirection:"column",
                  alignItems:"center",gap:'1px'}}>
                  <span style={{fontSize:'9px',fontWeight:800,color,
                    textAlign:"center",lineHeight:1.2}}>{name}</span>
                  <span style={{fontSize:6.5,color:COLORS.textMuted,
                    fontFamily:mono}}>{bw}</span>
                </div>
                {/* 내부 행 스택 */}
                <div style={{display:"flex",flexDirection:"column",
                  gap:'4px',padding:"6px",flex:1}}>
                  {children}
                </div>
              </div>
            );

            // 열 사이 수평 화살표
            const hArr = (color=COLORS.textMuted) => (
              <div style={{display:"flex",flexDirection:"column",
                alignItems:"center",justifyContent:"center",
                flexShrink:0,gap:'2px',padding:"0 2px",alignSelf:"center"}}>
                <span style={{fontSize:'14px',color,lineHeight:1}}>→</span>
              </div>
            );

            return (
              <div style={{marginBottom:'12px',padding:"9px 12px",borderRadius:'8px',
                background:COLORS.panel,border:`1px solid ${COLORS.border}`}}>

                {/* 헤더 */}
                <div style={{display:"flex",alignItems:"center",gap:'6px',marginBottom:'8px'}}>
                  <span style={{fontSize:'10px',fontWeight:700,color:COLORS.accent}}>
                    🏗️ {e("Cascade PID + SVPWM 제어 구조","Cascade PID + SVPWM Control Architecture")}
                  </span>
                  <span style={{fontSize:7.5,color:COLORS.textMuted}}>
                    {e("클릭 → 탭 이동","Click → tab")}
                  </span>
                  <div style={{marginLeft:"auto",padding:"2px 8px",borderRadius:'4px',
                    fontSize:7.5,fontFamily:mono,
                    background:sepOk?`${C_CUR}10`:"#fbbf2415",
                    border:`1px solid ${sepOk?C_CUR+'40':'#fbbf2450'}`,
                    color:sepOk?C_CUR:"#fbbf24"}}>
                    {sepOk?"✓":"⚠"} BW sep {sep.toFixed(1)}×
                  </div>
                </div>

                {/* ── 4열 수평 레이아웃 ── */}
                <div style={{display:"flex",alignItems:"stretch",gap:'2px'}}>

                  {/* 열1: 드라이버 (3행) */}
                  {col(e("드라이버","Driver"), "0.5~5 Hz", C_DRV, isDrv, <>
                    {cell("📥", e("목표속도","Target Speed"),       C_NEU, [])}
                    {cell("🎯", e("드라이버 PI/MPC","Driver PI/MPC"), C_DRV, ["torque_ref"])}
                    {cell("🎛️", e("DriveMode ×","DriveMode ×"),   "#f59e0b", [])}
                  </>)}

                  {hArr(C_SPD)}

                  {/* 열2: 속도루프 (3행) */}
                  {col(e("속도루프","Speed Loop"), "5~80 Hz", C_SPD, isSpd, <>
                    {cell("📡", e("현재속도","Actual Speed"), C_NEU, [])}
                    {cell("🔄", e("속도 PI","Speed PI"),   C_SPD, ["speed"])}
                    {cell("✅", e("BW 분리비","BW Sep"),    C_SPD, [])}
                  </>)}

                  {hArr(C_FW)}

                  {/* 열3: MTPA/MTPV/FW (4행) */}
                  {col("MTPA/MTPV/FW", e("전류 지령","Id/Iq*"), C_FW, isFW, <>
                    {cell("🧮", "MTPA",       C_FW,  ["fw_mtpa"])}
                    {cell("⚡", "MTPV",       C_FW,  ["fw_mtpa"])}
                    {cell("🌀", "Field Weak", C_FW,  ["fw_mtpa"])}
                  </>)}

                  {hArr(C_CUR)}

                  {/* 열4: 전류루프 (3행) */}
                  {col(e("전류루프","Current Loop"), "200~2kHz", C_CUR, isCur, <>
                    {cell("🔌", e("전류 PI","Current PI"),      C_CUR, ["current"])}
                    {cell("🔗", e("dq 디커플링","dq Decouple"),   C_CUR, ["current"])}
                    {cell("📶", "SVPWM",                         C_HW,  ["pwm"])}
                  </>)}

                </div>

                {/* MTPV/FW 파라미터 패널 */}
                <div style={{marginTop:'8px',padding:"5px 10px",borderRadius:'6px',
                  background:`${C_FW}08`,border:`1px solid ${C_FW}30`,
                  display:"flex",flexWrap:"wrap",gap:"1px 10px",
                  fontSize:7.5,fontFamily:mono}}>
                  <span style={{color:C_FW,fontWeight:700,flexShrink:0}}>
                    ⚡ MTPV/FW
                  </span>
                  {[
                    {k:"mtpv",  v:params.mtpv_enable!==false?"ON":"OFF",
                      c:params.mtpv_enable!==false?"#4ade80":"#f87171"},
                    {k:"fw",    v:params.fw_enable!==false?"ON":"OFF",
                      c:params.fw_enable!==false?"#4ade80":"#f87171"},
                    {k:"margin",v:(params.fw_v_margin??0.95).toFixed(2),    c:"#a78bfa"},
                    {k:"Vmax",  v:((params.V_dc??400)*(params.fw_v_margin??0.95)/Math.sqrt(3)).toFixed(0)+"V", c:"#a78bfa"},
                    {k:"λpm",   v:(params.lambda_pm??0.12).toFixed(3)+" Wb",c:"#a78bfa"},
                    {k:"Ld",    v:((params.Ld??0.0003)*1000).toFixed(2)+"mH",c:"#a78bfa"},
                    {k:"Lq",    v:((params.Lq??0.0006)*1000).toFixed(2)+"mH",c:"#a78bfa"},
                    {k:"I_max", v:(params.I_max_rms??600)+"A",             c:"#a78bfa"},
                  ].map(({k,v,c})=>(
                    <span key={k} style={{color:COLORS.textMuted}}>
                      {k}=<span style={{color:c,fontWeight:700}}>{v}</span>
                    </span>
                  ))}
                </div>

              </div>
            );
          })()}

          {subTab === "current" && (() => {
            // d/q 축 개별 Bode 계산
            const editModeGains = getModePIGains(piEditMode);
            const bodeParams = { ...params, ...editModeGains };
            // d축: Ld, Kp_id, Ki_id / q축: Lq, Kp_iq, Ki_iq
            const bodeParams_d = { ...bodeParams, Ld: bodeParams.Ld||0.0003,
              Kp_id: bodeParams.Kp_id||1.51, Ki_id: bodeParams.Ki_id||75 };
            const bodeParams_q = { ...bodeParams, Ld: bodeParams.Lq||0.0006,
              Kp_id: bodeParams.Kp_iq||3.02, Ki_id: bodeParams.Ki_iq||75 };
            const bode_dq = computeCurrentLoopBode_dq(bodeParams);
            const bode_d  = bode_dq[0];  // d축
            const bode_q  = bode_dq[1];  // q축
            const bode_i  = bode_d;      // 하위호환 (이론 지표 등)
            const PM_GOOD = 45, PM_WARN = 20, GM_GOOD = 6, GM_WARN = 2;
            const mkPmColor = (pm) => pm === null ? COLORS.textMuted
              : pm < PM_WARN ? "#ef4444" : pm < PM_GOOD ? "#f59e0b" : "#22c55e";
            const mkStatus  = (pm) => pm === null ? e("계산 중…","Computing…")
              : pm < PM_WARN ? e("⚠ 불안정","⚠ Unstable") : pm < PM_GOOD ? e("△ 한계","△ Marginal") : e("✓ 안정","✓ Stable");
            const pmColor = mkPmColor(bode_d.pm);
            const gmColor = bode_d.gm === null ? "#22c55e"
              : bode_d.gm < 2 ? "#ef4444" : bode_d.gm < 6 ? "#f59e0b" : "#22c55e";
            const statusLabel = mkStatus(bode_d.pm);
            const statusColor = mkPmColor(bode_d.pm);
            // [v9-fix4] IMC 자동 튜닝 함수
            const autoTuneCurrent = (bw_hz) => {
              const alpha = 2 * Math.PI * bw_hz;
              up("Kp_id", +(params.Ld * alpha).toFixed(2));
              up("Ki_id", +(params.Rs * alpha).toFixed(0));
              up("Kp_iq", +(params.Lq * alpha).toFixed(2));
              up("Ki_iq", +(params.Rs * alpha).toFixed(0));
            };
            return (
            <div>
              <ModalSectionTitle icon="🔌" title={e("d/q축 전류 제어기 — Current Loop PI","d/q-axis Current Controller — PI")} color={COLORS.accent} />
              {/* [A21] 자동 튜닝 적용 알림 배너 */}
              {params._autoTuned && (
                <div style={{ marginBottom:'8px', padding:"5px 10px", borderRadius:'5px', fontSize:9.5,
                  background:"#0d948820", border:"1px solid #0d948860", display:"flex", gap:'8px', alignItems:"center" }}>
                  <span style={{ color:"#2dd4bf", fontWeight:700 }}>✅ {e("IMC 자동 튜닝 적용됨","IMC Auto-Tune Applied")}</span>
                  <span style={{ color:COLORS.textMuted }}>Kp_iq={params.Kp_iq?.toFixed?.(4)} · Ki_iq={params.Ki_iq?.toFixed?.(2)} · Kp_id={params.Kp_id?.toFixed?.(4)}</span>
                </div>
              )}
              {/* [A10] 시뮬 직결 배너 */}
              <div style={{ marginBottom:'8px', padding:"5px 12px", borderRadius:'5px', fontSize:'9px',
                background:"#16a34a12", border:"1px solid #16a34a40", display:"flex", gap:'14px', flexWrap:"wrap" }}>
                <span style={{ color:"#4ade80", fontWeight:700 }}>🔗 {e("Kp_iq·Ki_iq → FOC Vq_cmd 직결","Kp_iq·Ki_iq → FOC Vq_cmd wired")}</span>
                <span style={{ color:"#60a5fa", fontFamily:"monospace" }}>Vq* = Kp·e_q + Ki·∫e_q + ωe(Ld·Id+λ)</span>
              </div>
              {/* [v9-fix5d] 전류 PI 연동 토글 */}
              <div style={{ marginBottom: '8px', padding: '8px', background: params.foc_current_pi_enable ? "#22c55e10" : "#fbbf2410", borderRadius: '6px', border: `1px solid ${params.foc_current_pi_enable ? "#22c55e40" : "#fbbf2440"}`, fontSize: '10px', color: params.foc_current_pi_enable ? "#22c55e" : "#fbbf24", fontFamily: mono, lineHeight: 1.6 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: '4px' }}>
                  <span>⚡ <strong>{e("시뮬레이션 연결 상태","Simulation Link Status")}</strong></span>
                  <button onClick={() => up("foc_current_pi_enable", !params.foc_current_pi_enable)}
                    style={{ padding: "3px 12px", borderRadius: '4px', cursor: "pointer", fontSize: '10px', fontWeight: 700,
                      background: params.foc_current_pi_enable ? "#22c55e" : COLORS.bg,
                      color: params.foc_current_pi_enable ? "#000" : COLORS.textDim,
                      border: `1px solid ${params.foc_current_pi_enable ? "#22c55e" : COLORS.border}` }}>
                    {e("전류 PI 연동","Current PI Link")}: {params.foc_current_pi_enable ? "ON ✓" : "OFF"}
                  </button>
                </div>
                {params.foc_current_pi_enable
                  ? <>{e("🟢 전류 PI 제어가","🟢 Current PI is")} <strong>{e("발진/재가속 시뮬레이션에 실제로 적용","actively applied in launch/reaccel sim")}</strong>. {e("dq축 전압 방정식, 디커플링, 안티와인드업이 포함됩니다. PI 게인이 부적절하면 전류 오버슈트/진동이 발생할 수 있습니다.","Includes dq voltage eqs, decoupling, anti-windup. Poor gains may cause overshoot/oscillation.")}</>
                  : <>{e("🟡 발진/코스트/재가속은","🟡 Launch/coast/reaccel uses")} <strong>{e("토크 제어 모드","torque control mode")}</strong>. {e("전류 PI를 직접 사용하지 않고 1차 지연 필터(τ=L/R)로 근사합니다. 아래 PI 게인은","Current PI not used directly; approximated by 1st-order filter (τ=L/R). PI gains below are")} <strong>{e("Bode 안정성 분석 전용","Bode stability analysis only")}</strong>. {e("연동하려면 위 토글을 ON하세요.","Enable toggle above to link.")}</>
                }
              </div>
              {/* ── 수식 설명 ── */}
              <div style={{ marginBottom: '10px', padding: '10px', background: `${COLORS.accent}08`, borderRadius: '6px', border: `1px solid ${COLORS.accent}20`, fontSize: '10px', color: COLORS.textDim, fontFamily: mono, lineHeight: 1.6 }}>
                📐 Id PI: Vd* = Kp_id·(Id*−Id) + Ki_id·∫(Id*−Id)dt − ωe·Lq·Iq<br/>
                📐 Iq PI: Vq* = Kp_iq·(Iq*−Iq) + Ki_iq·∫(Iq*−Iq)dt + ωe·(Ld·Id + λpm)<br/>
                {e("💡 IMC 설계: Kp = L×α, Ki = R×α (α = 2π × BW) — 위상여유 40~70° 목표","💡 IMC: Kp = L×α, Ki = R×α (α = 2π × BW) — target PM 40~70°")}<br/>
                {e("💡 BW 권장","💡 BW recommended")}: f_sw/10 ~ f_sw/5 ({(params.f_sw/10).toFixed(0)}~{(params.f_sw/5).toFixed(0)} Hz)
              </div>
              {/* IMC 자동 튜닝 — 모드별 독립 적용 */}
              {(() => {
                const BW_CUR_PRESETS = [
                  { l: e('보수적','Cons.'), bw: 500,  c: COLORS.accent3 },
                  { l: e('표준','Std'),     bw: 800,  c: COLORS.accent  },
                  { l: e('공격적','Aggr.'), bw: 1200, c: COLORS.accent4 },
                  { l: e('고속','Fast'),    bw: 2000, c: '#ef4444'       },
                ];
                const applyIMC_cur = (bw_hz) => {
                  const alpha = 2 * Math.PI * bw_hz;
                  upModeGain(piEditMode, 'Kp_id', +(params.Ld * alpha).toFixed(4));
                  upModeGain(piEditMode, 'Ki_id', +(params.Rs * alpha).toFixed(1));
                  upModeGain(piEditMode, 'Kp_iq', +(params.Lq * alpha).toFixed(4));
                  upModeGain(piEditMode, 'Ki_iq', +(params.Rs * alpha).toFixed(1));
                  if (params._autoTuned) up('_autoTuned', null);
                };
                const editMGains = getModePIGains(piEditMode);
                const fc_d = editMGains.Kp_id / (params.Ld||0.0003) / (2*Math.PI);
                const fc_q = editMGains.Kp_iq / (params.Lq||0.0006) / (2*Math.PI);
                const modeColor = {ECO:'#22c55e',NORMAL:'#60a5fa',SPORT:'#f97316',
                  SNOW:'#38bdf8',OFF_ROAD:'#a855f7'}[piEditMode]||'#60a5fa';
                return (
                  <div style={{ marginBottom:'10px', padding:'8px 10px', borderRadius:'7px',
                    background:`${modeColor}08`, border:`1px solid ${modeColor}30` }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap', marginBottom:'4px' }}>
                      <span style={{ fontSize:9.5, fontWeight:700, color:modeColor }}>
                        🎯 IMC {e('자동튜닝','Auto-Tune')} — {piEditMode} {e('모드','mode')}
                      </span>
                      <span style={{ fontSize:'8px', color:COLORS.textMuted, fontFamily:mono }}>
                        d fc≈<span style={{color:modeColor,fontWeight:700}}>{fc_d.toFixed(0)}Hz</span>
                        {' · '}q fc≈<span style={{color:modeColor,fontWeight:700}}>{fc_q.toFixed(0)}Hz</span>
                      </span>
                      <span style={{flex:1}}/>
                      {BW_CUR_PRESETS.map((p,i) => {
                        const fc_d_p = (params.Ld||0.0003)*2*Math.PI*p.bw/(2*Math.PI*(params.Ld||0.0003)) * p.bw;
                        return (
                          <button key={i} onClick={() => applyIMC_cur(p.bw)}
                            style={{ padding:'4px 10px', borderRadius:'5px', cursor:'pointer',
                              fontSize:9.5, fontWeight:600,
                              background:`${p.c}15`, color:p.c, border:`1px solid ${p.c}40`,
                              display:'flex', flexDirection:'column', alignItems:'center', gap:0 }}>
                            <span>{p.l}</span>
                            <span style={{fontSize:7.5, opacity:0.8}}>{p.bw>=1000?(p.bw/1000).toFixed(1)+'kHz':p.bw+'Hz'}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ fontSize:7.5, color:COLORS.textMuted, fontFamily:mono }}>
                      ℹ️ {e(`${piEditMode} 모드의 Kp_id·Ki_id·Kp_iq·Ki_iq에 직접 기입 (커스텀 저장). Ld·Lq 비대칭 반영.`,
                            `Writes to ${piEditMode} mode Kp/Ki_id/iq (saved as custom). Reflects Ld≠Lq asymmetry.`)}
                    </div>
                  </div>
                );
              })()}
              {/* ── 모드별 PI 슬라이더 ── */}
              {(() => {
                const MODES = [
                  { id:'ECO',      icon:'🌿', label: e('에코','ECO'),       color:'#22c55e' },
                  { id:'NORMAL',   icon:'🚗', label: e('노멀','NORMAL'),    color:'#60a5fa' },
                  { id:'SPORT',    icon:'🏎️', label: e('스포츠','SPORT'),   color:'#f97316' },
                  { id:'SNOW',     icon:'❄️', label: e('스노우','SNOW'),    color:'#38bdf8' },
                  { id:'OFF_ROAD', icon:'🏔️', label: e('오프로드','OFF-RD'),color:'#a855f7' },
                ];
                const activeDriveMode = (params.drive_mode || 'NORMAL').toUpperCase();
                // piEditMode 초기화: subTab 진입 시 현재 drive_mode로 맞춤
                const editM = piEditMode;
                const mGains = getModePIGains(editM);
                const mColor = MODES.find(m => m.id === editM)?.color || '#60a5fa';
                const mIcon  = MODES.find(m => m.id === editM)?.icon || '🚗';
                const isCustom = !!params.mode_gains?.[editM];
                const alpha800 = 2 * Math.PI * 800;
                return (
                <div style={{ marginBottom:'10px' }}>
                  {/* 모드 탭 바 */}
                  <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'10px',
                    padding:'6px 10px', borderRadius:'8px', background:COLORS.panel,
                    border:`1px solid ${COLORS.border}` }}>
                    <span style={{ fontSize:'10px', color:COLORS.textMuted, marginRight:'4px', fontWeight:600 }}>
                      {e('모드별 설정:','Per-Mode:')}
                    </span>
                    {MODES.map(m => {
                      const isActive = editM === m.id;
                      const isDriving = activeDriveMode === m.id;
                      const hasCustom = !!params.mode_gains?.[m.id];
                      return (
                        <button key={m.id}
                          onClick={() => setPiEditMode(m.id)}
                          style={{
                            padding:'5px 14px', borderRadius:'7px', border:'none', cursor:'pointer',
                            fontWeight: isActive ? 700 : 500, fontSize:'11px',
                            background: isActive ? m.color : `${m.color}18`,
                            color: isActive ? '#fff' : m.color,
                            boxShadow: isActive ? `0 0 0 2px ${m.color}60` : 'none',
                            transition:'all 0.15s', position:'relative',
                          }}>
                          {m.icon} {m.label}
                          {isDriving && (
                            <span style={{ position:'absolute', top:-4, right:-4,
                              width:8, height:8, borderRadius:'50%',
                              background: '#facc15', border:`1.5px solid ${COLORS.bg}` }} />
                          )}
                          {hasCustom && !isDriving && (
                            <span style={{ position:'absolute', top:-4, right:-4,
                              width:7, height:7, borderRadius:'50%',
                              background: m.color, border:`1.5px solid ${COLORS.bg}`, opacity:0.7 }} />
                          )}
                        </button>
                      );
                    })}
                    <span style={{ flex:1 }}/>
                    <div style={{ display:'flex', gap:'5px' }}>
                      <button onClick={() => resetModeToDefault(editM)}
                        title={e(`${editM} 모드만 자동 연산값으로 재계산 (커스텀 삭제)`,`Recalc ${editM} mode only (clears custom)`)}
                        style={{ padding:'4px 10px', borderRadius:'5px',
                          border:`1px solid ${isCustom ? mColor+'80' : COLORS.border}`,
                          background: isCustom ? `${mColor}18` : COLORS.bg,
                          color: isCustom ? mColor : COLORS.textMuted,
                          fontSize:9.5, cursor:'pointer', fontWeight: isCustom ? 700 : 400 }}>
                        ↺ {e('이 모드 재계산','Recalc This')}
                        {isCustom && <span style={{ marginLeft:'4px', fontSize:'8px', opacity:0.8 }}>{e('(커스텀 해제)','(clear custom)')}</span>}
                      </button>
                      <button onClick={resetAllModesToDefault}
                        title={e('전체 모드 커스텀 삭제 후 자동 연산값으로 교체','Recalc all modes, clears all custom values')}
                        style={{ padding:'4px 10px', borderRadius:'5px', border:`1px solid ${COLORS.border}`,
                          background:COLORS.bg, color:COLORS.textMuted, fontSize:9.5, cursor:'pointer' }}>
                        {e('전체 재계산','Recalc All')}
                      </button>
                    </div>
                  </div>

                  {/* 현재 편집 모드 상태 배지 */}
                  <div style={{ marginBottom:'8px', padding:'5px 12px', borderRadius:'6px',
                    background:`${mColor}12`, border:`1px solid ${mColor}40`,
                    display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
                    <span style={{ fontSize:'11px', fontWeight:700, color:mColor }}>
                      {mIcon} {MODES.find(m=>m.id===editM)?.label} {e('모드 전류 PI 설정','Mode Current PI')}
                    </span>
                    {activeDriveMode === editM && (
                      <span style={{ fontSize:'9px', background:'#facc1522', border:'1px solid #facc1540',
                        borderRadius:'4px', padding:'2px 7px', color:'#facc15', fontWeight:700 }}>
                        ● {e('현재 주행 모드','Active Drive Mode')}
                      </span>
                    )}
                    {isCustom && (
                      <span style={{ fontSize:'9px', background:`${mColor}22`, border:`1px solid ${mColor}40`,
                        borderRadius:'4px', padding:'2px 7px', color:mColor, fontWeight:600 }}>
                        ✎ {e('커스텀 설정됨','Custom Set')}
                      </span>
                    )}
                    {!isCustom && (
                      <span style={{ fontSize:'9px', color:COLORS.textMuted }}>
                        {e('배율 자동 계산 (편집 시 저장됨)','Auto from multiplier (saved on edit)')}
                      </span>
                    )}
                    {/* 배율 참조 */}
                    <span style={{ fontSize:8.5, color:COLORS.textDim, fontFamily:"'JetBrains Mono',monospace",
                      borderLeft:`1px solid ${COLORS.border}`, paddingLeft:'8px' }}>
                      ×{DRIVE_MODE_GAIN_TABLE[editM]?.Kp_iq?.toFixed(2)} Kp &nbsp;
                      ×{DRIVE_MODE_GAIN_TABLE[editM]?.Ki_iq?.toFixed(2)} Ki
                    </span>
                  </div>

                  {/* PI 슬라이더 (선택된 모드) */}
                  <div style={{ display:"grid", minWidth:0,
                    gridTemplateColumns:GRID.auto2, gap:"0 24px" }}>
                    <PI label={e("Kp_id (d축 비례)","Kp_id (d-axis P)")}
                        value={mGains.Kp_id}
                        onChange={v => upModeGain(editM, 'Kp_id', v)}
                        unit="" step={0.1} min={0.1} max={80}
                        desc={`IMC: Ld×α = ${(params.Ld * alpha800).toFixed(2)}`} />
                    <PI label={e("Ki_id (d축 적분)","Ki_id (d-axis I)")}
                        value={mGains.Ki_id}
                        onChange={v => upModeGain(editM, 'Ki_id', v)}
                        unit="" step={5} min={5} max={8000}
                        desc={`IMC: Rs×α = ${(params.Rs * alpha800).toFixed(0)}`} />
                    <PI label={e("Kp_iq (q축 비례)","Kp_iq (q-axis P)")}
                        value={mGains.Kp_iq}
                        onChange={v => upModeGain(editM, 'Kp_iq', v)}
                        unit="" step={0.1} min={0.1} max={80}
                        desc={`IMC: Lq×α = ${(params.Lq * alpha800).toFixed(2)}`} />
                    <PI label={e("Ki_iq (q축 적분)","Ki_iq (q-axis I)")}
                        value={mGains.Ki_iq}
                        onChange={v => upModeGain(editM, 'Ki_iq', v)}
                        unit="" step={5} min={5} max={8000}
                        desc={`IMC: Rs×α = ${(params.Rs * alpha800).toFixed(0)}`} />
                  </div>

                  {/* 모드별 비교 요약표 */}
                  <div style={{ marginTop:'12px', padding:'8px 12px', borderRadius:'7px',
                    background:COLORS.panel, border:`1px solid ${COLORS.border}` }}>
                    <div style={{ fontSize:9.5, fontWeight:700, color:COLORS.textMuted, marginBottom:'6px' }}>
                      📊 {e('모드별 전류 PI 비교','Current PI by Mode')}
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'5px', minWidth:0 }}>
                      {MODES.map(m => {
                        const g = getModePIGains(m.id);
                        const isEd = editM === m.id;
                        const isDrv = activeDriveMode === m.id;
                        return (
                          <div key={m.id}
                            onClick={() => setPiEditMode(m.id)}
                            style={{ padding:'6px 8px', borderRadius:'6px', cursor:'pointer', minWidth:0,
                              background: isEd ? `${m.color}18` : COLORS.bg,
                              border:`1px solid ${isEd ? m.color : COLORS.border}`,
                              transition:'all 0.15s' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:'3px', marginBottom:'3px', flexWrap:'nowrap', overflow:'hidden' }}>
                              <span style={{ fontSize:9.5, fontWeight:700, color:m.color, whiteSpace:'nowrap' }}>{m.icon} {m.label}</span>
                              {isDrv && <span style={{ fontSize:'7px', color:'#facc15', flexShrink:0 }}>●</span>}
                              {!!params.mode_gains?.[m.id] && <span style={{ fontSize:'7px', color:m.color, flexShrink:0 }}>✎</span>}
                            </div>
                            {[
                              { k:'Kp_id', v:g.Kp_id },
                              { k:'Ki_id', v:g.Ki_id },
                              { k:'Kp_iq', v:g.Kp_iq },
                              { k:'Ki_iq', v:g.Ki_iq },
                            ].map(({ k, v }) => (
                              <div key={k} style={{ display:'flex', justifyContent:'space-between', gap:'2px',
                                fontSize:'8px', color:isEd ? COLORS.text : COLORS.textMuted,
                                fontFamily:"'JetBrains Mono',monospace" }}>
                                <span style={{ flexShrink:0 }}>{k}</span>
                                <span style={{ fontWeight:700, color: isEd ? m.color : COLORS.textMuted, textAlign:'right' }}>
                                  {typeof v === 'number' ? (v >= 100 ? v.toFixed(1) : v.toFixed(3)) : '—'}
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                );
              })()}

              {/* ── 레거시 IMC 프리셋 버튼 (NORMAL 베이스 일괄 적용) ── */}
              <div style={{ marginTop: '8px', display: "grid", minWidth: 0, gridTemplateColumns: "repeat(auto-fit, minmax(min(140px, 40vw), 1fr))", gap: '6px' }}>
                <div style={{ gridColumn:'1/-1', fontSize:'9px', color:COLORS.textMuted, marginBottom:'3px' }}>
                  🔧 {e('전류 PI IMC 프리셋 — NORMAL 베이스 일괄 적용 (전 모드 재계산됨)','Current PI IMC Preset — Bulk NORMAL base (all modes recalculated)')}
                </div>
                {[
                  { l: e("보수적 (BW≈500Hz)","Conservative (BW≈500Hz)"), kp: +(params.Ld*Math.PI*1000).toFixed(2), ki: +(params.Rs*Math.PI*1000).toFixed(0), c: COLORS.accent3 },
                  { l: e("표준 (BW≈800Hz)","Standard (BW≈800Hz)"), kp: +(params.Ld*2*Math.PI*800).toFixed(2), ki: +(params.Rs*2*Math.PI*800).toFixed(0), c: COLORS.accent },
                  { l: e("공격적 (BW≈1.5kHz)","Aggressive (BW≈1.5kHz)"), kp: +(params.Ld*2*Math.PI*1500).toFixed(2), ki: +(params.Rs*2*Math.PI*1500).toFixed(0), c: COLORS.accent4 },
                ].map((p, i) => (
                  <button key={i} onClick={() => {
                    up("Kp_id", p.kp); up("Ki_id", p.ki);
                    up("Kp_iq", +(p.kp * params.Lq / params.Ld).toFixed(2)); up("Ki_iq", p.ki);
                    // 모든 모드 재계산
                    setTimeout(resetAllModesToDefault, 50);
                    // 수동 프리셋 적용 시 IMC 자동 튜닝 알림 초기화
                    if (params._autoTuned) up("_autoTuned", null);
                  }}
                    style={{ padding: "7px", borderRadius: '6px', cursor: "pointer", background: COLORS.bg,
                      border: `1px solid ${COLORS.border}`, color: p.c, fontSize: '10px', fontFamily: mono, textAlign: "center" }}>
                    <div style={{ fontWeight: 700 }}>{p.l}</div>
                    <div style={{ fontSize: '9px', color: COLORS.textMuted }}>Kp={p.kp} Ki={p.ki}</div>
                  </button>
                ))}
                {/* 전류 PI 프리셋 설명 */}
                <div style={{ gridColumn:'1/-1', marginTop:'4px', padding:"4px 10px", borderRadius:'5px', fontSize:8.5,
                  background:COLORS.cardBg, border:`1px solid ${COLORS.border}`,
                  color:COLORS.textMuted, lineHeight:1.6 }}>
                  ℹ️ {e(
                    "이 프리셋은 Kp_id·Ki_id·Kp_iq·Ki_iq 기본값을 덮어씁니다. 모든 드라이브 모드가 NORMAL 베이스 기준으로 재계산됩니다.",
                    "These presets overwrite Kp_id·Ki_id·Kp_iq·Ki_iq base values. All drive modes are recalculated from the NORMAL base."
                  )}
                </div>
              </div>

              {/* ════════════════════════════════════════════════
                  ▼ 보드 선도 (전류 루프) — d축 / q축 개별
                  ════════════════════════════════════════════════ */}
              <div style={{ marginTop: '14px', borderTop: `1px solid ${COLORS.accent}30`, paddingTop: '12px' }}>
                {/* 헤더 */}
                <div style={{ display:"flex", alignItems:"center", gap:'8px', marginBottom:'8px', flexWrap:"wrap" }}>
                  <span style={{ fontSize:'13px', fontWeight:800, color:"#a78bfa" }}>📈 {e("보드 선도","Bode Plot")}</span>
                  <span style={{ fontSize:'9px', color:COLORS.textMuted, fontFamily:mono }}>L(jω) = C·G·e^(−1.5Ts·jω)</span>
                  {(() => {
                    const mc = {ECO:'#22c55e',NORMAL:'#60a5fa',SPORT:'#f97316',SNOW:'#38bdf8',OFF_ROAD:'#a855f7'}[piEditMode]||'#60a5fa';
                    const mi = {ECO:'🌿',NORMAL:'🚗',SPORT:'🏎️',SNOW:'❄️',OFF_ROAD:'🏔️'}[piEditMode]||'🚗';
                    return <span style={{fontSize:'9px',background:`${mc}18`,border:`1px solid ${mc}40`,
                      borderRadius:'4px',padding:'2px 8px',color:mc,fontWeight:600}}>
                      {mi} {piEditMode}</span>;
                  })()}
                  {/* d축/q축 안정성 배지 */}
                  <div style={{marginLeft:"auto",display:"flex",gap:'6px',flexWrap:"wrap"}}>
                    {[{ax:"d",b:bode_d,c:COLORS.accent},{ax:"q",b:bode_q,c:"#ec4899"}].map(({ax,b,c})=>(
                      <div key={ax} style={{background:COLORS.bg,
                        border:`1px solid ${mkPmColor(b.pm)}50`,
                        borderRadius:'6px',padding:"3px 8px",
                        display:"flex",gap:'6px',alignItems:"center"}}>
                        <span style={{fontSize:'9px',fontWeight:800,color:c}}>{ax}축</span>
                        <span style={{fontSize:'9px',fontWeight:700,color:mkPmColor(b.pm)}}>
                          {mkStatus(b.pm)}</span>
                        <span style={{fontSize:8.5,fontFamily:mono,color:COLORS.textDim}}>
                          PM:<span style={{color:mkPmColor(b.pm),fontWeight:700}}>
                            {b.pm!==null?`${b.pm}°`:'N/A'}</span>
                          {" "}BW:<span style={{color:c,fontWeight:700}}>
                            {b.bw!==null?(b.bw>=1000?`${(b.bw/1000).toFixed(1)}kHz`:`${b.bw}Hz`):'N/A'}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 이론 지표 — d축/q축 분리 */}
                <div style={{ display:"flex", gap:'6px', marginBottom:'8px', flexWrap:"wrap" }}>
                  {[
                    {ax:"d",b:bode_d,L:params.Ld||0.0003,Kp:editModeGains.Kp_id,c:COLORS.accent},
                    {ax:"q",b:bode_q,L:params.Lq||0.0006,Kp:editModeGains.Kp_iq,c:"#ec4899"},
                  ].map(({ax,b,L,Kp,c})=>(
                    <div key={ax} style={{background:COLORS.bg,border:`1px solid ${c}30`,
                      borderRadius:'6px',padding:"5px 9px",fontSize:8.5,fontFamily:mono,flex:"1 1 0",minWidth:0}}>
                      <div style={{fontWeight:700,color:c,marginBottom:'3px'}}>{ax}축 (L{ax}={((L)*1000).toFixed(2)}mH)</div>
                      <div style={{display:"flex",gap:'8px',flexWrap:"wrap"}}>
                        <span style={{color:COLORS.textMuted}}>BW:<span style={{color:c,fontWeight:700}}>
                          {b.bw_theory>=1000?(b.bw_theory/1000).toFixed(1)+'kHz':b.bw_theory+'Hz'}</span></span>
                        <span style={{color:COLORS.textMuted}}>영점:<span style={{color:'#a78bfa',fontWeight:700}}>{b.zero_freq}Hz</span></span>
                        <span style={{color:COLORS.textMuted}}>극점:<span style={{color:COLORS.textDim,fontWeight:700}}>
                          {((params.Rs||0.015)/L/(2*Math.PI)).toFixed(0)}Hz</span></span>
                        <span style={{color:COLORS.textMuted}}>1.5T:<span style={{color:'#f59e0b',fontWeight:700}}>
                          {((1.5*(params.ctrl_dt||1e-4))*1e6).toFixed(0)}µs</span></span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* 2×2: d축 이득/위상 + q축 이득/위상 */}
                <div style={{ display:"grid", gridTemplateColumns:"minmax(0,1fr) minmax(0,1fr)", gap:'8px', alignItems:"start" }}>

                  <div style={{ background:COLORS.bg, borderRadius:'7px', padding:"8px 10px", border:"1px solid "+COLORS.accent+"30" }}>
                    <div style={{ fontSize:'10px', color:COLORS.accent, fontFamily:mono, marginBottom:'4px', fontWeight:700 }}>
                      {"📈 "+e("d축 이득선도","d-axis Gain")+" |L_d(jω)|"}
                    </div>
                    <ResponsiveContainer width="100%" height={160}>
                      <LineChart data={bode_d.data} margin={{ top:4, right:6, left:0, bottom:8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} />
                        <XAxis dataKey="freq_hz" scale="log" type="number" domain={bode_d.f_range}
                          tickFormatter={v => v>=1000?`${(v/1000).toFixed(0)}k`:`${v>=1?v.toFixed(0):v.toFixed(2)}`}
                          tick={{ fontSize:'8px', fill:COLORS.textMuted }} />
                        <YAxis tick={{ fontSize:'8px', fill:COLORS.textMuted }} />
                        <Tooltip formatter={v=>[`${v} dB`]} labelFormatter={v=>`${Number(v).toFixed(1)} Hz`}
                          contentStyle={{ background:COLORS.panel, border:"1px solid "+COLORS.border, fontSize:'9px' }} />
                        <ReferenceLine y={0} stroke="#f59e0b" strokeDasharray="4 2" strokeWidth={1.5} />
                        {bode_d.bw_theory && <ReferenceLine x={bode_d.bw_theory} stroke="#f87171" strokeDasharray="2 2" strokeWidth={1.5}
                          label={{ value:`fc:${bode_d.bw_theory>=1000?(bode_d.bw_theory/1000).toFixed(1)+"kHz":bode_d.bw_theory+"Hz"}`, fill:"#f87171", fontSize:'7px', position:"insideTopLeft" }} />}
                        <Line type="monotone" dataKey="mag_dB" stroke={COLORS.accent} dot={false} strokeWidth={2} name="|L_d|" />
                        <Line type="monotone" dataKey="T_dB" stroke="#22c55e" dot={false} strokeWidth={1.5} strokeDasharray="5 3" name="|T_d(CL)|" />
                        <Legend iconSize={8} wrapperStyle={{ fontSize:'8px' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div style={{ background:COLORS.bg, borderRadius:'7px', padding:"8px 10px", border:"1px solid "+COLORS.accent+"30" }}>
                    <div style={{ fontSize:'10px', color:"#a78bfa", fontFamily:mono, marginBottom:'4px', fontWeight:700 }}>
                      {"📉 "+e("d축 위상선도","d-axis Phase")+" ∠L_d(jω)"}
                    </div>
                    <ResponsiveContainer width="100%" height={160}>
                      <LineChart data={bode_d.data} margin={{ top:4, right:6, left:0, bottom:8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} />
                        <XAxis dataKey="freq_hz" scale="log" type="number" domain={bode_d.f_range}
                          tickFormatter={v => v>=1000?`${(v/1000).toFixed(0)}k`:`${v>=1?v.toFixed(0):v.toFixed(2)}`}
                          tick={{ fontSize:'8px', fill:COLORS.textMuted }} />
                        <YAxis domain={[-270,0]} ticks={[-270,-180,-90,0]} tick={{ fontSize:'8px', fill:COLORS.textMuted }} />
                        <Tooltip formatter={v=>[`${v}°`]} labelFormatter={v=>`${Number(v).toFixed(1)} Hz`}
                          contentStyle={{ background:COLORS.panel, border:"1px solid "+COLORS.border, fontSize:'9px' }} />
                        <ReferenceLine y={-180} stroke="#ef4444" strokeDasharray="4 2" strokeWidth={1.5} />
                        <ReferenceLine y={-135} stroke="#f59e0b" strokeDasharray="3 3" strokeWidth={1} />
                        {bode_d.bw_theory && <ReferenceLine x={bode_d.bw_theory} stroke="#f87171" strokeDasharray="2 2" strokeWidth={1.5}
                          label={{ value:`fc:${bode_d.bw_theory>=1000?(bode_d.bw_theory/1000).toFixed(1)+"kHz":bode_d.bw_theory+"Hz"}`, fill:"#f87171", fontSize:'7px', position:"insideTopLeft" }} />}
                        <Line type="monotone" dataKey="phase_deg" stroke={COLORS.isDark?"#a78bfa":"#5b21b6"} dot={false} strokeWidth={2} name="∠L_d" />
                        <Legend iconSize={8} wrapperStyle={{ fontSize:'8px' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div style={{ background:COLORS.bg, borderRadius:'7px', padding:"8px 10px", border:"1px solid #ec489930" }}>
                    <div style={{ fontSize:'10px', color:"#ec4899", fontFamily:mono, marginBottom:'4px', fontWeight:700 }}>
                      {"📈 "+e("q축 이득선도","q-axis Gain")+" |L_q(jω)|"}
                    </div>
                    <ResponsiveContainer width="100%" height={160}>
                      <LineChart data={bode_q.data} margin={{ top:4, right:6, left:0, bottom:8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} />
                        <XAxis dataKey="freq_hz" scale="log" type="number" domain={bode_q.f_range}
                          tickFormatter={v => v>=1000?`${(v/1000).toFixed(0)}k`:`${v>=1?v.toFixed(0):v.toFixed(2)}`}
                          tick={{ fontSize:'8px', fill:COLORS.textMuted }} />
                        <YAxis tick={{ fontSize:'8px', fill:COLORS.textMuted }} />
                        <Tooltip formatter={v=>[`${v} dB`]} labelFormatter={v=>`${Number(v).toFixed(1)} Hz`}
                          contentStyle={{ background:COLORS.panel, border:"1px solid "+COLORS.border, fontSize:'9px' }} />
                        <ReferenceLine y={0} stroke="#f59e0b" strokeDasharray="4 2" strokeWidth={1.5} />
                        {bode_q.bw_theory && <ReferenceLine x={bode_q.bw_theory} stroke="#f87171" strokeDasharray="2 2" strokeWidth={1.5}
                          label={{ value:`fc:${bode_q.bw_theory>=1000?(bode_q.bw_theory/1000).toFixed(1)+"kHz":bode_q.bw_theory+"Hz"}`, fill:"#f87171", fontSize:'7px', position:"insideTopLeft" }} />}
                        <Line type="monotone" dataKey="mag_dB" stroke="#ec4899" dot={false} strokeWidth={2} name="|L_q|" />
                        <Line type="monotone" dataKey="T_dB" stroke="#22c55e" dot={false} strokeWidth={1.5} strokeDasharray="5 3" name="|T_q(CL)|" />
                        <Legend iconSize={8} wrapperStyle={{ fontSize:'8px' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div style={{ background:COLORS.bg, borderRadius:'7px', padding:"8px 10px", border:"1px solid #ec489930" }}>
                    <div style={{ fontSize:'10px', color:"#a78bfa", fontFamily:mono, marginBottom:'4px', fontWeight:700 }}>
                      {"📉 "+e("q축 위상선도","q-axis Phase")+" ∠L_q(jω)"}
                    </div>
                    <ResponsiveContainer width="100%" height={160}>
                      <LineChart data={bode_q.data} margin={{ top:4, right:6, left:0, bottom:8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} />
                        <XAxis dataKey="freq_hz" scale="log" type="number" domain={bode_q.f_range}
                          tickFormatter={v => v>=1000?`${(v/1000).toFixed(0)}k`:`${v>=1?v.toFixed(0):v.toFixed(2)}`}
                          tick={{ fontSize:'8px', fill:COLORS.textMuted }} />
                        <YAxis domain={[-270,0]} ticks={[-270,-180,-90,0]} tick={{ fontSize:'8px', fill:COLORS.textMuted }} />
                        <Tooltip formatter={v=>[`${v}°`]} labelFormatter={v=>`${Number(v).toFixed(1)} Hz`}
                          contentStyle={{ background:COLORS.panel, border:"1px solid "+COLORS.border, fontSize:'9px' }} />
                        <ReferenceLine y={-180} stroke="#ef4444" strokeDasharray="4 2" strokeWidth={1.5} />
                        <ReferenceLine y={-135} stroke="#f59e0b" strokeDasharray="3 3" strokeWidth={1} />
                        {bode_q.bw_theory && <ReferenceLine x={bode_q.bw_theory} stroke="#f87171" strokeDasharray="2 2" strokeWidth={1.5}
                          label={{ value:`fc:${bode_q.bw_theory>=1000?(bode_q.bw_theory/1000).toFixed(1)+"kHz":bode_q.bw_theory+"Hz"}`, fill:"#f87171", fontSize:'7px', position:"insideTopLeft" }} />}
                        <Line type="monotone" dataKey="phase_deg" stroke="#ec4899" dot={false} strokeWidth={2} name="∠L_q" />
                        <Legend iconSize={8} wrapperStyle={{ fontSize:'8px' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                </div>
                {/* 설계 가이드 */}
                <div style={{ marginTop: '8px', padding: '8px', background: COLORS.bg, borderRadius: '6px', border: `1px solid ${COLORS.border}`, fontSize: '9px', color: COLORS.textDim, fontFamily: mono, lineHeight: 1.8 }}>
                  <span style={{ fontWeight: 700, color: COLORS.text }}>{e("⚙️ 설계 가이드라인","⚙️ Design Guidelines")}</span>
                  <br/>• BW(Kp/L/2π) = <span style={{ color: COLORS.accent }}>{bode_i.bw_theory >= 1000 ? `${(bode_i.bw_theory/1000).toFixed(1)} kHz` : `${bode_i.bw_theory} Hz`}</span> — {e("PWM 주파수","PWM freq.")}({`${((1/(params.ctrl_dt||1e-4))/1000).toFixed(0)} kHz`}) {e("의 1/5~1/10 이하 권장","1/5~1/10 recommended")}
                  <br/>• {e("영점 주파수(Ki/Kp/2π) ≪ BW: 현재","Zero freq (Ki/Kp/2π) ≪ BW: current")} <span style={{ color: "#a78bfa" }}>{bode_i.zero_freq} Hz</span> {bode_i.zero_freq < bode_i.bw_theory/10 ? "✓" : e("⚠ BW의 1/10 이상 — Ki 낮추기 권장","⚠ ≥ BW/10 — reduce Ki")}
                  <br/>• {e("위상여유 목표","Target PM")}: 45°~70° | {e("이득여유 목표","Target GM")}: &gt;6 dB | {e("1.5T 지연에 의한 위상손실","Phase loss due to 1.5T delay")}: <span style={{ color: '#f59e0b' }}>{bode_i.bw_theory !== null ? `${(bode_i.bw_theory * 2 * Math.PI * 1.5 * (params.ctrl_dt||1e-4) * 180/Math.PI).toFixed(1)}°` : "N/A"}</span> @ BW
                </div>
              </div>
            </div>
            );
          })()}

          {/* ── [Fix-M2] 반복제어 / 센서리스 PLL — current 탭 하단 섹션 ── */}
          {subTab === "current" && (
            <div style={{ marginTop:'14px', padding:"10px 14px", borderRadius:'8px',
              border:`1px solid #a78bfa40`, background:"#a78bfa08" }}>
              <div style={{ fontSize:'11px', fontWeight:700, color:"#a78bfa", marginBottom:'8px' }}>
                🔁 {e("반복제어 (Repetitive Control) — 고조파 제거","Repetitive Control — Harmonic Rejection")}
              </div>
              <div style={{ fontSize:'9px', color:COLORS.textMuted, marginBottom:'8px', lineHeight:1.7,
                fontFamily:"monospace", padding:"5px 8px", borderRadius:'4px', background:"#a78bfa0a" }}>
                {e("u_rep(k) = Q·u_rep(k-N) + Kr·e(k)  (N = f_sw/f_fund, 기본파 주기 샘플 수)",
                   "u_rep(k) = Q·u_rep(k-N) + Kr·e(k)  (N = f_sw/f_fund, samples per fundamental)")}<br/>
                {e("Q < 1: 안정성 마진 확보 | Kr: 고조파 제거 속도 — 높을수록 빠르나 오버슈트 증가",
                   "Q < 1: stability margin | Kr: harmonic rejection speed — higher = faster but more overshoot")}
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:'10px', marginBottom:'8px', flexWrap:"wrap" }}>
                <button onClick={()=>up("harmonic_rejection_enable",!(params.harmonic_rejection_enable??false))}
                  style={{ padding:"3px 12px", borderRadius:'5px', border:"none", cursor:"pointer",
                    fontSize:'10px', fontWeight:700,
                    background:(params.harmonic_rejection_enable??false)?"#a78bfa":COLORS.border,
                    color:(params.harmonic_rejection_enable??false)?"#fff":COLORS.textMuted }}>
                  {e("반복제어","Rep.Ctrl")}: {(params.harmonic_rejection_enable??false)?"ON":"OFF"}
                </button>
                {(params.harmonic_rejection_enable??false) && (
                  <span style={{fontSize:8.5,color:"#a78bfa"}}>
                    N = {Math.round((params.f_sw||10000)/(params.poles||8)/2)} {e("샘플/주기","samples/cycle")}
                    {" | "}f_fund ≈ {((params.f_sw||10000)/(params.poles||8)/2).toFixed(0)} Hz
                  </span>
                )}
              </div>
              {(params.harmonic_rejection_enable??false) && (
                <div style={{ display:"grid", gridTemplateColumns:GRID.auto2, gap:"0 24px" }}>
                  <PI label={e("반복제어 게인 Kr","Repetitive Gain Kr")}
                    value={params.Kr_repetitive??0.3}
                    onChange={v=>up("Kr_repetitive",v)}
                    unit="" step={0.05} min={0.0} max={1.0}
                    desc={e("0.1~0.5 권장. 높을수록 고조파 제거 빠르나 불안정 위험","0.1~0.5 typical. Higher = faster harmonic rejection, but risk of instability")} />
                  <PI label={e("안정화 필터 Q","Stability Filter Q")}
                    value={params.Q_repetitive??0.95}
                    onChange={v=>up("Q_repetitive",v)}
                    unit="" step={0.01} min={0.80} max={1.0}
                    desc={e("< 1 유지 필수 (안정성). 0.95~0.98 일반적","Must keep < 1 for stability. 0.95~0.98 typical")} />
                </div>
              )}

              {/* 센서리스 PLL */}
              <div style={{ marginTop:'12px', borderTop:`1px solid #a78bfa20`, paddingTop:'10px' }}>
                <div style={{ fontSize:'10px', fontWeight:700, color:"#a78bfa", marginBottom:'6px' }}>
                  📡 {e("센서리스 PLL 게인 (sensorless_enable=ON 시 적용)","Sensorless PLL Gains (active when sensorless_enable=ON)")}
                </div>
                <div style={{ display:"grid", gridTemplateColumns:GRID.auto2, gap:"0 24px" }}>
                  <PI label="PLL Kp"
                    value={params.Kp_pll??100}
                    onChange={v=>up("Kp_pll",v)}
                    unit="" step={10} min={10} max={1000}
                    desc={e("역기전력 관측기 PLL 비례 게인","Back-EMF observer PLL proportional gain")} />
                  <PI label="PLL Ki"
                    value={params.Ki_pll??2000}
                    onChange={v=>up("Ki_pll",v)}
                    unit="" step={100} min={100} max={10000}
                    desc={e("PLL 적분 게인 — BW ≈ √Ki/2π","PLL integral gain — BW ≈ √Ki/2π")} />
                </div>
                <div style={{ marginTop:'6px', fontSize:8.5, color:COLORS.textMuted }}>
                  {e("현재 sensorless_enable","Current sensorless_enable")}: {" "}
                  <span style={{fontWeight:700, color:(params.sensorless_enable)?"#4ade80":"#f87171"}}>
                    {params.sensorless_enable?"ON":"OFF"}
                  </span>
                  {!params.sensorless_enable && ` — ${e("Vehicle Info 모달에서 활성화","enable in Vehicle Info modal")}`}
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════
              [A16] 토크 참조 생성 탭 (torque_ref)
              VCU입력 → 드라이브모드/회생맵 적용 → T_ref 생성 블럭에 대응
          ══════════════════════════════════════════════════════════ */}
          {subTab === "torque_ref" && (() => {
            return (
              <div>
                <ModalSectionTitle icon="🎯"
                  title={e("토크 참조 생성 — VCU 입력 → T_ref","Torque Reference Generation — VCU Input → T_ref")}
                  color="#22c55e" />
                {/* 흐름 설명 */}
                <div style={{ marginBottom:'14px', padding:"10px 14px", borderRadius:'8px',
                  background:"#22c55e10", border:"1px solid #22c55e30",
                  fontSize:'10px', color:COLORS.textDim, fontFamily:mono, lineHeight:1.9 }}>
                  <div style={{ fontWeight:700, color:"#22c55e", marginBottom:'4px' }}>
                    📐 {e("T_ref 생성 흐름","T_ref Generation Flow")}
                  </div>
                  T_cmd (VCU) → 드라이브 모드 적용 (max_torque_ratio · pedal_sensitivity)<br/>
                  → APP/BPP 필터링 (app_deadzone · ROC 제한 · tau_torque)<br/>
                  → 회생 맵 적용 (regen_strength × blending)<br/>
                  → <span style={{color:'#f59e0b',fontWeight:700}}>T_ref</span> → 속도 PI Outer Loop (ω_ref 결정)
                </div>
                {/* [Fix-M3] 토크 참조 파라미터 슬라이더 */}
                <div style={{ marginBottom:'14px', padding:"10px 14px", borderRadius:'8px',
                  background:COLORS.surface, border:"1px solid #22c55e30" }}>
                  <div style={{ fontSize:'10px', fontWeight:700, color:"#22c55e", marginBottom:'8px' }}>
                    ⚙️ {e("토크 참조 생성 파라미터","Torque Reference Parameters")}
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:GRID.auto2, gap:"0 24px" }}>
                    <PI label={e("슬루율 제한 (Torque Rate)","Torque Rate Limit")}
                      value={params.torque_rate_limit??500}
                      onChange={v=>up("torque_rate_limit",v)}
                      unit="Nm/s" step={50} min={50} max={5000}
                      desc={e("T_ref 변화율 제한 — 낮을수록 부드러운 토크 전환","T_ref rate limit — lower = smoother torque transition")} />
                    <PI label={e("위치 제어 Kp","Position Kp")}
                      value={params.Kp_pos??5.0}
                      onChange={v=>up("Kp_pos",v)}
                      unit="" step={0.5} min={0.5} max={50}
                      desc={e("위치 루프 비례 게인 (cascade_speed_enable=OFF 시 미사용)","Position loop Kp (unused when cascade_speed_enable=OFF)")} />
                    <PI label={e("위치 제어 Ki","Position Ki")}
                      value={params.Ki_pos??0.5}
                      onChange={v=>up("Ki_pos",v)}
                      unit="" step={0.1} min={0} max={10}
                      desc={e("위치 루프 적분 게인","Position loop Ki")} />
                    <PI label={e("위치 제어 Kd","Position Kd")}
                      value={params.Kd_pos??0.05}
                      onChange={v=>up("Kd_pos",v)}
                      unit="" step={0.01} min={0} max={2.0}
                      desc={e("위치 루프 미분 게인 (진동 억제)","Position loop Kd (oscillation suppression)")} />
                  </div>
                  <div style={{ marginTop:'6px', fontSize:8.5, color:COLORS.textMuted }}>
                    ℹ️ {e("torque_rate_limit은 속도 PI 탭에서도 편집 가능 (동일 파라미터)","torque_rate_limit also editable in Speed PI tab (same param)")}
                  </div>
                </div>

                {/* 관련 탭 안내 */}
                <div style={{ padding:"10px 14px", borderRadius:'8px',
                  background:COLORS.panel, border:`1px solid ${COLORS.border}`,
                  fontSize:9.5, color:COLORS.textMuted, lineHeight:2 }}>
                  💡 {e("이 탭의 파라미터는 관련 탭에 통합되어 있습니다","Parameters for this block are in the related tabs below")}
                  <div style={{ display:"flex", gap:'8px', marginTop:'8px', flexWrap:"wrap" }}>
                    {[
                      { tab:"sig_input",  group:"drive", color:"#64748b", icon:"🧑‍✈️", label:e("운전자 입력 / 신호 처리","Driver Input / Signal") },
                      { tab:"drv_mode",   group:"drive", color:"#22c55e", icon:"🎛️", label:e("드라이브 모드","Drive Mode") },
                      { tab:"speed",      group:"motor", color:COLORS.accent2, icon:"🔄", label:e("속도 PI →","Speed PI →") },
                    ].map(({tab,group,color,icon,label}) => (
                      <button key={tab}
                        onClick={() => { if(group !== "motor") setGroup(group); setSubTab(tab); }}
                        style={{ padding:"5px 12px", borderRadius:'5px', cursor:"pointer", fontSize:'9px',
                          background:`${color}15`, color, border:`1px solid ${color}40`, fontWeight:600 }}>
                        {icon} {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {subTab === "speed" && (() => {
            // Bode 계산 시 현재 편집 중인 모드의 게인 사용
            const editModeGains = getModePIGains(piEditMode);
            const bodeParams = { ...params, ...editModeGains };
            const bode_s = computeSpeedLoopBode(bodeParams);
            const bode_i = computeCurrentLoopBode(bodeParams);  // 전류 루프 BW 참조용
            const PM_GOOD = 45, PM_WARN = 20, GM_GOOD = 6, GM_WARN = 2;
            const pmColor = bode_s.pm === null ? COLORS.textMuted
              : bode_s.pm < PM_WARN ? "#ef4444" : bode_s.pm < PM_GOOD ? "#f59e0b" : "#22c55e";
            const gmDisplay = bode_s.gm_inf ? e("∞ (조건부 안정)","∞ (conditionally stable)") : (bode_s.gm !== null ? `${bode_s.gm} dB` : "N/A");
            const gmColor   = bode_s.gm_inf
              ? (bode_s.phase_margin_to_180 < 1 ? "#f59e0b" : "#22c55e")  // -180° 근접도로 색상
              : (bode_s.gm < GM_WARN ? "#ef4444" : bode_s.gm < GM_GOOD ? "#f59e0b" : "#22c55e");
            const statusLabel = bode_s.pm === null ? "계산 중…"
              : bode_s.pm < PM_WARN ? e("⚠ 불안정","⚠ Unstable") : bode_s.pm < PM_GOOD ? e("△ 한계","△ Marginal") : e("✓ 안정","✓ Stable");
            const statusColor = bode_s.pm === null ? COLORS.textMuted
              : bode_s.pm < PM_WARN ? "#ef4444" : bode_s.pm < PM_GOOD ? "#f59e0b" : "#22c55e";
            // 속도루프 BW 권장 상한: 전류루프 BW / 10
            const bw_i_hz    = bode_i.bw_theory;
            const bw_spd_rec = bw_i_hz ? (bw_i_hz / 10) : null;
            return (
            <div>
              <ModalSectionTitle icon="🔄" title={e("속도 제어기 — Speed Loop PI","Speed Controller — PI")} color={COLORS.accent2} />
              {/* [A21] 자동 튜닝 적용 알림 배너 */}
              {params._autoTuned && (
                <div style={{ marginBottom:'8px', padding:"5px 10px", borderRadius:'5px', fontSize:9.5,
                  background:"#0d948820", border:"1px solid #0d948860", display:"flex", gap:'8px', alignItems:"center" }}>
                  <span style={{ color:"#2dd4bf", fontWeight:700 }}>✅ {e("IMC 자동 튜닝 적용됨","IMC Auto-Tune Applied")}</span>
                  <span style={{ color:COLORS.textMuted }}>Kp_spd={params.Kp_spd?.toFixed?.(3)} · Ki_spd={params.Ki_spd?.toFixed?.(1)}</span>
                </div>
              )}
              {/* [A10] 시뮬 직결 배너 */}
              <div style={{ marginBottom:'8px', padding:"5px 12px", borderRadius:'5px', fontSize:'9px',
                background:"#16a34a12", border:"1px solid #16a34a40", display:"flex", gap:'14px', flexWrap:"wrap" }}>
                <span style={{ color:"#4ade80", fontWeight:700 }}>🔗 {e("Kp_spd·Ki_spd → 속도 PI 루프 직결","Kp_spd·Ki_spd → Speed PI directly wired")}</span>
                <span style={{ color:"#34d399", fontFamily:"monospace" }}>Iq* = Kp_spd·(ω*−ω) + Ki_spd·∫(ω*−ω)</span>
              </div>
              {/* [v9-fix4b] 제어기 연결 상태 표시 */}
              <div style={{ marginBottom: '8px', padding: '8px', background: "#fbbf2410", borderRadius: '6px', border: "1px solid #fbbf2440", fontSize: '10px', color: "#fbbf24", fontFamily: mono, lineHeight: 1.6 }}>
                {e("⚡ 시뮬레이션 연결 상태:","⚡ Simulation Link Status:")} {e("발진/코스트/재가속은","Launch/coast/reaccel uses")} <strong>{e("토크 제어 모드","torque control mode")}</strong>. {e("속도 PI를 사용하지 않습니다.","Speed PI is not used.")}
                {e("WLTP/FTP 사이클에서는 별도 드라이버 모델(wltp_tracking_kp/ki)이 속도 추종을 담당합니다.","WLTP/FTP cycles use a separate driver model (wltp_tracking_kp/ki) for speed tracking.")}
                {e("아래 PI 게인은","PI gains below are for")} <strong>{e("Bode 안정성 분석","Bode stability analysis")}</strong> {e("과 벡터 제어 설계 참조용입니다.","and vector control reference.")}
                {params.foc_current_pi_enable && e(" 단, 전류 PI 연동 모드 활성화 시 dq 전류 루프는 실제로 사용됩니다."," Note: with current PI link ON, dq current loop is actually used.")}
              </div>
              {/* ── 수식 설명 ── */}
              <div style={{ marginBottom: '10px', padding: '10px', background: `${COLORS.accent2}08`, borderRadius: '6px', border: `1px solid ${COLORS.accent2}20`, fontSize: '10px', color: COLORS.textDim, fontFamily: mono, lineHeight: 1.6 }}>
                📐 Iq* = Kp_spd·(ω*−ω) + Ki_spd·∫(ω*−ω)dt → 전류 루프 Iq 지령<br/>
                {e("💡 외부 루프: 전류 루프 BW의 1/5~1/10 이하 설계 (루프 분리 조건)","💡 Outer loop: design at ≤1/5~1/10 of current loop BW (loop separation condition)")}<br/>
                {e("💡 IMC 설계: Kp_spd = J_eq × α_s,  Ki_spd = Kp × α_s / 3  (α_s = 2π × BW_spd)","💡 IMC: Kp_spd = J_eq × α_s,  Ki_spd = Kp × α_s / 3  (α_s = 2π × BW_spd)")}<br/>
                {e("💡 J_eq","💡 J_eq")} = J_motor + m·r²/gr² = {bode_s.J_eq.toFixed(3)} kg·m²
              </div>

              {/* ── 모드별 PI 슬라이더 ── */}
              {(() => {
                const MODES = [
                  { id:'ECO',      icon:'🌿', label: e('에코','ECO'),       color:'#22c55e' },
                  { id:'NORMAL',   icon:'🚗', label: e('노멀','NORMAL'),    color:'#60a5fa' },
                  { id:'SPORT',    icon:'🏎️', label: e('스포츠','SPORT'),   color:'#f97316' },
                  { id:'SNOW',     icon:'❄️', label: e('스노우','SNOW'),    color:'#38bdf8' },
                  { id:'OFF_ROAD', icon:'🏔️', label: e('오프로드','OFF-RD'),color:'#a855f7' },
                ];
                const activeDriveMode = (params.drive_mode || 'NORMAL').toUpperCase();
                const editM = piEditMode;
                const mGains = getModePIGains(editM);
                const mColor = MODES.find(m => m.id === editM)?.color || '#60a5fa';
                const mIcon  = MODES.find(m => m.id === editM)?.icon || '🚗';
                const isCustom = !!params.mode_gains?.[editM];
                const J_eq_val = ((params.J_motor||0.05) + params.m*params.r_wheel*params.r_wheel/(params.gear_ratio*params.gear_ratio));
                return (
                <div style={{ marginBottom:'10px' }}>
                  {/* 모드 탭 바 */}
                  <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'10px',
                    padding:'6px 10px', borderRadius:'8px', background:COLORS.panel,
                    border:`1px solid ${COLORS.border}` }}>
                    <span style={{ fontSize:'10px', color:COLORS.textMuted, marginRight:'4px', fontWeight:600 }}>
                      {e('모드별 설정:','Per-Mode:')}
                    </span>
                    {MODES.map(m => {
                      const isActive = editM === m.id;
                      const isDriving = activeDriveMode === m.id;
                      const hasCustom = !!params.mode_gains?.[m.id];
                      return (
                        <button key={m.id}
                          onClick={() => setPiEditMode(m.id)}
                          style={{
                            padding:'5px 14px', borderRadius:'7px', border:'none', cursor:'pointer',
                            fontWeight: isActive ? 700 : 500, fontSize:'11px',
                            background: isActive ? m.color : `${m.color}18`,
                            color: isActive ? '#fff' : m.color,
                            boxShadow: isActive ? `0 0 0 2px ${m.color}60` : 'none',
                            transition:'all 0.15s', position:'relative',
                          }}>
                          {m.icon} {m.label}
                          {isDriving && (
                            <span style={{ position:'absolute', top:-4, right:-4,
                              width:8, height:8, borderRadius:'50%',
                              background:'#facc15', border:`1.5px solid ${COLORS.bg}` }} />
                          )}
                          {hasCustom && !isDriving && (
                            <span style={{ position:'absolute', top:-4, right:-4,
                              width:7, height:7, borderRadius:'50%',
                              background:m.color, border:`1.5px solid ${COLORS.bg}`, opacity:0.7 }} />
                          )}
                        </button>
                      );
                    })}
                    <span style={{ flex:1 }}/>
                    <div style={{ display:'flex', gap:'5px' }}>
                      <button onClick={() => resetModeToDefault(editM)}
                        title={e(`${editM} 모드만 자동 연산값으로 재계산 (커스텀 삭제)`,`Recalc ${editM} mode only (clears custom)`)}
                        style={{ padding:'4px 10px', borderRadius:'5px',
                          border:`1px solid ${isCustom ? mColor+'80' : COLORS.border}`,
                          background: isCustom ? `${mColor}18` : COLORS.bg,
                          color: isCustom ? mColor : COLORS.textMuted,
                          fontSize:9.5, cursor:'pointer', fontWeight: isCustom ? 700 : 400 }}>
                        ↺ {e('이 모드 재계산','Recalc This')}
                        {isCustom && <span style={{ marginLeft:'4px', fontSize:'8px', opacity:0.8 }}>{e('(커스텀 해제)','(clear custom)')}</span>}
                      </button>
                      <button onClick={resetAllModesToDefault}
                        title={e('전체 모드 커스텀 삭제 후 자동 연산값으로 교체','Recalc all modes, clears all custom values')}
                        style={{ padding:'4px 10px', borderRadius:'5px', border:`1px solid ${COLORS.border}`,
                          background:COLORS.bg, color:COLORS.textMuted, fontSize:9.5, cursor:'pointer' }}>
                        {e('전체 재계산','Recalc All')}
                      </button>
                    </div>
                  </div>

                  {/* 현재 편집 모드 상태 배지 */}
                  <div style={{ marginBottom:'8px', padding:'5px 12px', borderRadius:'6px',
                    background:`${mColor}12`, border:`1px solid ${mColor}40`,
                    display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
                    <span style={{ fontSize:'11px', fontWeight:700, color:mColor }}>
                      {mIcon} {MODES.find(m=>m.id===editM)?.label} {e('모드 속도 PI 설정','Mode Speed PI')}
                    </span>
                    {activeDriveMode === editM && (
                      <span style={{ fontSize:'9px', background:'#facc1522', border:'1px solid #facc1540',
                        borderRadius:'4px', padding:'2px 7px', color:'#facc15', fontWeight:700 }}>
                        ● {e('현재 주행 모드','Active Drive Mode')}
                      </span>
                    )}
                    {isCustom && (
                      <span style={{ fontSize:'9px', background:`${mColor}22`, border:`1px solid ${mColor}40`,
                        borderRadius:'4px', padding:'2px 7px', color:mColor, fontWeight:600 }}>
                        ✎ {e('커스텀 설정됨','Custom Set')}
                      </span>
                    )}
                    {!isCustom && (
                      <span style={{ fontSize:'9px', color:COLORS.textMuted }}>
                        {e('배율 자동 계산 (편집 시 저장됨)','Auto from multiplier (saved on edit)')}
                      </span>
                    )}
                    <span style={{ fontSize:8.5, color:COLORS.textDim, fontFamily:"'JetBrains Mono',monospace",
                      borderLeft:`1px solid ${COLORS.border}`, paddingLeft:'8px' }}>
                      ×{DRIVE_MODE_GAIN_TABLE[editM]?.Kp_spd?.toFixed(2)} Kp &nbsp;
                      ×{DRIVE_MODE_GAIN_TABLE[editM]?.Ki_spd?.toFixed(2)} Ki
                    </span>
                  </div>

                  {/* ── IMC BW 프리셋 (모드별 독립 적용) ── */}
                  {(() => {
                    const BW_PRESETS = [
                      { l: e('보수적','Cons.'), bw: 5,  c: COLORS.accent3 },
                      { l: e('표준','Std'),     bw: 10, c: COLORS.accent2 },
                      { l: e('빠른','Fast'),    bw: 20, c: COLORS.accent4 },
                      { l: e('공격적','Aggr.'), bw: 50, c: '#ef4444' },
                    ];
                    const applyIMC = (bw_hz) => {
                      const alpha_s = 2 * Math.PI * bw_hz;
                      const Kp_new = +(J_eq_val * alpha_s).toFixed(1);
                      const Ki_new = +(J_eq_val * alpha_s * alpha_s / 3).toFixed(0);
                      upModeGain(editM, 'Kp_spd', Kp_new);
                      upModeGain(editM, 'Ki_spd', Ki_new);
                      if (params._autoTuned) up('_autoTuned', null);
                    };
                    // 현재 모드 Kp로부터 현재 fc 역산
                    const fc_current = mGains.Kp_spd / (2 * Math.PI * J_eq_val);
                    return (
                    <div style={{ marginBottom:'10px', padding:'8px 10px', borderRadius:'7px',
                      background:`${mColor}08`, border:`1px solid ${mColor}30` }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
                        <span style={{ fontSize:9.5, fontWeight:700, color:mColor }}>
                          🎯 IMC {e('자동튜닝','Auto-Tune')} — {mIcon} {MODES.find(m=>m.id===editM)?.label}
                        </span>
                        <span style={{ fontSize:8.5, color:COLORS.textMuted, fontFamily:mono }}>
                          {e('현재 fc','current fc')} ≈ <span style={{ color:mColor, fontWeight:700 }}>{fc_current.toFixed(1)} Hz</span>
                        </span>
                        <span style={{ flex:1 }} />
                        {BW_PRESETS.map((p, i) => {
                          const Kp_p = J_eq_val * 2 * Math.PI * p.bw;
                          const fc_p = Kp_p / (2 * Math.PI * J_eq_val);
                          const isActive = Math.abs(fc_current - fc_p) < 0.5;
                          return (
                            <button key={i} onClick={() => applyIMC(p.bw)}
                              style={{ padding:'4px 10px', borderRadius:'5px', cursor:'pointer',
                                fontSize:9.5, fontWeight: isActive ? 800 : 600,
                                background: isActive ? p.c : `${p.c}15`,
                                color: isActive ? '#fff' : p.c,
                                border:`1px solid ${isActive ? p.c : p.c+'40'}`,
                                display:'flex', flexDirection:'column', alignItems:'center', gap:0 }}>
                              <span>{p.l}</span>
                              <span style={{ fontSize:7.5, opacity: isActive ? 1 : 0.8 }}>fc={fc_p.toFixed(0)}Hz</span>
                            </button>
                          );
                        })}
                      </div>
                      <div style={{ marginTop:'5px', fontSize:'8px', color:COLORS.textMuted, fontFamily:mono }}>
                        ℹ️ {e(
                          `${editM} 모드의 Kp_spd·Ki_spd에 직접 기입됩니다 (커스텀으로 저장). 다른 모드는 영향 없음.`,
                          `Writes directly to ${editM} mode Kp_spd·Ki_spd (saved as custom). Other modes unaffected.`
                        )}
                      </div>
                    </div>
                    );
                  })()}

                  {/* ── 전류+속도 동시 IMC 일괄 적용 ── */}
                  {(() => {
                    const J_eq_bulk = (params.J_motor||0.05)
                      + (params.m||1850)*(params.r_wheel||0.33)**2/(params.gear_ratio||9.5)**2;
                    const BULK_PRESETS = [
                      { l:e("보수적","Cons."),  bw_c:500,  bw_s:5,  c:COLORS.accent3 },
                      { l:e("표준","Std"),      bw_c:800,  bw_s:10, c:COLORS.accent2 },
                      { l:e("공격적","Aggr."),  bw_c:1500, bw_s:20, c:COLORS.accent4 },
                    ];
                    const applyBulk = (bw_c, bw_s) => {
                      // 전류 루프 (d/q 축)
                      const ac = 2*Math.PI*bw_c;
                      up("Kp_id", +(params.Ld*ac).toFixed(4));
                      up("Ki_id", +(params.Rs*ac).toFixed(1));
                      up("Kp_iq", +(params.Lq*ac).toFixed(4));
                      up("Ki_iq", +(params.Rs*ac).toFixed(1));
                      // 속도 루프
                      const as = 2*Math.PI*bw_s;
                      up("Kp_spd", +(J_eq_bulk*as).toFixed(1));
                      up("Ki_spd", +(J_eq_bulk*as*as/3).toFixed(0));
                      // 전 모드 재계산 + 타임스탬프
                      setTimeout(resetAllModesToDefault, 50);
                      up("_autoTuned", Date.now());
                    };
                    return (
                      <div style={{ marginTop:'10px', marginBottom:'8px', padding:"8px 10px",
                        borderRadius:'7px', background:COLORS.cardBg, border:`1px solid ${COLORS.border}` }}>
                        <div style={{ fontSize:9.5, fontWeight:700, color:COLORS.textMuted, marginBottom:'6px' }}>
                          🔧 {e("전류 + 속도 동시 IMC 일괄 적용","Current + Speed simultaneous IMC bulk apply")}
                        </div>
                        <div style={{ display:"flex", gap:'6px', flexWrap:"wrap", marginBottom:'6px' }}>
                          {BULK_PRESETS.map((p,i) => (
                            <button key={i} onClick={()=>applyBulk(p.bw_c, p.bw_s)}
                              style={{ padding:"5px 12px", borderRadius:'5px', cursor:"pointer",
                                fontSize:9.5, fontWeight:600,
                                background:`${p.c}15`, color:p.c, border:`1px solid ${p.c}40` }}>
                              <div>{p.l}</div>
                              <div style={{fontSize:7.5, opacity:0.8}}>
                                {e(`전류 ${p.bw_c>=1000?(p.bw_c/1000).toFixed(1)+"k":p.bw_c}Hz / 속도 ${p.bw_s}Hz`,
                                   `Cur ${p.bw_c>=1000?(p.bw_c/1000).toFixed(1)+"k":p.bw_c}Hz / Spd ${p.bw_s}Hz`)}
                              </div>
                            </button>
                          ))}
                        </div>
                        <div style={{ fontSize:'8px', color:COLORS.textMuted, lineHeight:1.6 }}>
                          ℹ️ {e(
                            "현재 모터 파라미터(Ld·Lq·Rs·J_eq) 기준으로 전류 PI(d/q)·속도 PI를 한 번에 계산·기입합니다. 전 드라이브 모드 NORMAL 베이스 재계산 포함.",
                            "Computes and applies current PI (d/q) and speed PI simultaneously from current motor params (Ld·Lq·Rs·J_eq). Recalculates all drive modes from NORMAL base."
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* PI 슬라이더 */}
                  <div style={{ display:"grid", minWidth:0,
                    gridTemplateColumns:GRID.auto2, gap:"0 24px" }}>
                    <PI label={e("Kp_spd (속도 비례)","Kp_spd (speed P)")}
                        value={mGains.Kp_spd}
                        onChange={v => upModeGain(editM, 'Kp_spd', v)}
                        unit="" step={1} min={0.5} max={800}
                        desc={`IMC Kp = J_eq×2π×fc`} />
                    <PI label={e("Ki_spd (속도 적분)","Ki_spd (speed I)")}
                        value={mGains.Ki_spd}
                        onChange={v => upModeGain(editM, 'Ki_spd', v)}
                        unit="" step={50} min={10} max={30000} />
                    <PI label={e("토크 슬루율 제한","Torque Slew Rate Limit")}
                        value={params.torque_rate_limit}
                        onChange={v => up("torque_rate_limit", v)}
                        unit="Nm/s" step={500} min={500} max={20000} />
                  </div>

                  {/* 모드별 비교 요약표 */}
                  <div style={{ marginTop:'12px', padding:'8px 12px', borderRadius:'7px',
                    background:COLORS.panel, border:`1px solid ${COLORS.border}` }}>
                    <div style={{ fontSize:9.5, fontWeight:700, color:COLORS.textMuted, marginBottom:'6px' }}>
                      📊 {e('모드별 속도 PI 비교','Speed PI by Mode')}
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'5px', minWidth:0 }}>
                      {MODES.map(m => {
                        const g = getModePIGains(m.id);
                        const isEd = editM === m.id;
                        const isDrv = activeDriveMode === m.id;
                        // 올바른 공식: fc = Kp/(2π·J_eq)  (ω<<wci 조건, Gi≈1)
                        const bw_spd_est = g.Kp_spd / (2 * Math.PI * J_eq_val);
                        return (
                          <div key={m.id}
                            onClick={() => setPiEditMode(m.id)}
                            style={{ padding:'6px 8px', borderRadius:'6px', cursor:'pointer', minWidth:0,
                              background: isEd ? `${m.color}18` : COLORS.bg,
                              border:`1px solid ${isEd ? m.color : COLORS.border}`,
                              transition:'all 0.15s' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:'3px', marginBottom:'3px', flexWrap:'nowrap', overflow:'hidden' }}>
                              <span style={{ fontSize:9.5, fontWeight:700, color:m.color, whiteSpace:'nowrap' }}>{m.icon} {m.label}</span>
                              {isDrv && <span style={{ fontSize:'7px', color:'#facc15', flexShrink:0 }}>●</span>}
                              {!!params.mode_gains?.[m.id] && <span style={{ fontSize:'7px', color:m.color, flexShrink:0 }}>✎</span>}
                            </div>
                            {[
                              { k:'Kp_spd', v:g.Kp_spd },
                              { k:'Ki_spd', v:g.Ki_spd },
                              { k:'BW≈', v: bw_spd_est.toFixed(1)+'Hz' },
                            ].map(({ k, v }) => (
                              <div key={k} style={{ display:'flex', justifyContent:'space-between', gap:'2px',
                                fontSize:'8px', color:isEd ? COLORS.text : COLORS.textMuted,
                                fontFamily:"'JetBrains Mono',monospace" }}>
                                <span style={{ flexShrink:0 }}>{k}</span>
                                <span style={{ fontWeight:700, color: isEd ? m.color : COLORS.textMuted, textAlign:'right' }}>
                                  {typeof v === 'number' ? (v >= 100 ? v.toFixed(1) : v.toFixed(2)) : v}
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                );
              })()}

              {/* ════════════════════════════════════════════════
                  ▼ 보드 선도 (속도 루프) — 인라인 임베드
                  ════════════════════════════════════════════════ */}
              <div style={{ marginTop: '14px', borderTop: `1px solid ${COLORS.accent2}30`, paddingTop: '12px' }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: '8px', flexWrap: "wrap", gap: '6px' }}>
                  <div style={{ display: "flex", alignItems: "center", gap: '8px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 800, color: "#a78bfa" }}>{e("📈 보드 선도","📈 Bode Plot")}</span>
                    <span style={{ fontSize: '9px', color: COLORS.textMuted, fontFamily: mono }}>L_s(jω) = C_s·Gi·Gm</span>
                    {(() => {
                      const mc = {ECO:'#22c55e',NORMAL:'#60a5fa',SPORT:'#f97316',SNOW:'#38bdf8',OFF_ROAD:'#a855f7'}[piEditMode]||'#60a5fa';
                      const mi = {ECO:'🌿',NORMAL:'🚗',SPORT:'🏎️',SNOW:'❄️',OFF_ROAD:'🏔️'}[piEditMode]||'🚗';
                      return (
                        <span style={{ fontSize:'9px', background:`${mc}18`, border:`1px solid ${mc}40`,
                          borderRadius:'4px', padding:'2px 8px', color:mc, fontWeight:600 }}>
                          {mi} {piEditMode} {e('모드 기준','mode')}</span>
                      );
                    })()}
                  </div>
                  {/* 안정성 배지 */}
                  <div style={{ background: COLORS.bg, border: `1px solid ${statusColor}50`, borderRadius: '6px', padding: "4px 10px", display: "flex", gap: '10px', alignItems: "center" }}>
                    <span style={{ fontSize: '13px', fontWeight: 900, color: statusColor }}>{statusLabel}</span>
                    <span style={{ fontSize: '10px', fontFamily: mono, color: COLORS.textDim }}>
                      PM: <span style={{ color: pmColor, fontWeight: 700 }}>{bode_s.pm !== null ? `${bode_s.pm}°` : "N/A"}</span>
                      {"  "}GM: <span style={{ color: gmColor, fontWeight: 700 }}>{gmDisplay}</span>
                      {"  "}BW: <span style={{ color: COLORS.accent2, fontWeight: 700 }}>{bode_s.bw !== null ? (bode_s.bw >= 1 ? `${Number(bode_s.bw).toFixed(2)} Hz` : `${(bode_s.bw*1000).toFixed(1)} mHz`) : "N/A"}</span>
                    </span>
                  </div>
                </div>

                {/* GM=∞ 물리 해석 박스 */}
                {bode_s.gm_inf && (
                  <div style={{ marginBottom: '8px', padding: "6px 10px", background: "#22c55e0a", border: "1px solid #22c55e30", borderRadius: '6px', fontSize: '9px', color: COLORS.textDim, fontFamily: mono, lineHeight: 1.7 }}>
                    <span style={{ color: "#22c55e", fontWeight: 700 }}>ℹ GM = ∞ {e("(위상 −180° 미통과)","(phase never crosses −180°)")}</span> — {e("속도 루프 PI + 적분 플랜트 조합의 정상 동작","normal behavior for speed PI + integrating plant")}<br/>
                    {e("저주파 위상 최솟값:","Low-freq phase minimum:")} <span style={{ color: bode_s.phase_margin_to_180 < 1 ? "#f59e0b" : "#22c55e", fontWeight: 700 }}>{(180 - bode_s.phase_margin_to_180 - 180) < 0 ? `−${(180-bode_s.phase_margin_to_180).toFixed(2)}°` : `${(180-bode_s.phase_margin_to_180).toFixed(2)}°`}</span>
                    {" "}({e("−180°까지 여유","−180° margin")}: <span style={{ color: bode_s.phase_margin_to_180 < 1 ? "#f59e0b" : "#22c55e" }}>{bode_s.phase_margin_to_180.toFixed(2)}°</span>)
                    {bode_s.phase_margin_to_180 < 2 && <span style={{ color: "#f59e0b" }}> {e("⚠ −180°에 매우 근접 — Ki_spd 낮추기 권장","⚠ Very close to −180° — reduce Ki_spd")}</span>}
                  </div>
                )}

                {/* 이론 지표 행 */}
                <div style={{ display: "flex", gap: '8px', marginBottom: '8px', flexWrap: "wrap" }}>
                  {[
                    { l: e("등가 관성 J_eq","Equiv. Inertia J_eq"),         v: `${bode_s.J_eq.toFixed(3)} kg·m²`, c: COLORS.accent2 },
                    { l: e("전류 루프 BW (ωc_i/2π)","Current Loop BW (ωc_i/2π)"), v: bw_i_hz >= 1000 ? `${(bw_i_hz/1000).toFixed(1)} kHz` : `${bw_i_hz} Hz`, c: COLORS.accent },
                    { l: e("속도 루프 BW 권장 상한","Spd Loop BW Limit"),  v: bw_spd_rec ? `${bw_spd_rec >= 1 ? bw_spd_rec.toFixed(1) : (bw_spd_rec*1000).toFixed(0)+" m"}Hz` : "N/A", c: "#f59e0b" },
                    { l: e("이론 교차주파수 fc (√(Kp·wci/J)/2π)","Theoretical fc"), v: bode_s.bw_theory ? `${bode_s.bw_theory} Hz` : "N/A", c: "#f87171" },
                    { l: e("PI 영점 (Ki/Kp/2π)","PI Zero (Ki/Kp/2π)"),  v: bode_s.zero_freq ? `${bode_s.zero_freq} Hz` : "N/A", c: "#a78bfa" },
                    { l: e("폐루프 −3dB BW","Closed-loop −3dB BW"), v: bode_s.bw ? (bode_s.bw >= 1 ? `${bode_s.bw} Hz` : `${(bode_s.bw*1000).toFixed(1)} mHz`) : "N/A",
                      c: bode_s.bw && bw_spd_rec && bode_s.bw > bw_spd_rec ? "#ef4444" : "#22c55e" },
                  ].map((m, i) => (
                    <div key={i} style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: '5px', padding: "4px 8px", fontSize: '9px', fontFamily: mono }}>
                      <span style={{ color: COLORS.textMuted }}>{m.l}: </span>
                      <span style={{ color: m.c, fontWeight: 700 }}>{m.v}</span>
                    </div>
                  ))}
                </div>

                {/* [A19] 속도 PI 보드선도 2열: 이득선도(좌) + 위상선도(우) */}
                <div style={{ display:"grid", gridTemplateColumns:"minmax(0,1fr) minmax(0,1fr)", gap:'10px', alignItems:"start" }}>
                  {/* 좌: 이득 선도 */}
                  <div style={{ background:COLORS.bg, borderRadius:'7px', padding:"8px 10px", border:`1px solid ${COLORS.border}` }}>
                    <div style={{ fontSize:'10px', color:COLORS.accent2, fontFamily:mono, marginBottom:'4px', fontWeight:700 }}>
                      📈 {e("이득 선도","Gain Plot")} — |L_s(jω)| dB
                    </div>
                    <ResponsiveContainer width="100%" height={160}>
                      <LineChart data={bode_s.data} margin={{ top:4, right:6, left:0, bottom:8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} />
                        <XAxis dataKey="freq_hz" scale="log" type="number" domain={bode_s.f_range}
                          tickFormatter={v => v >= 1 ? v.toFixed(1) : v >= 0.01 ? v.toFixed(3) : v.toExponential(1)}
                          tick={{ fontSize: '8px', fill: COLORS.textMuted }}
                          label={{ value: "Hz (log)", position: "insideRight", offset: -2, fontSize: '8px', fill: COLORS.textMuted }} />
                        <YAxis tick={{ fontSize: '8px', fill: COLORS.textMuted }}
                          label={{ value: "dB", angle: -90, position: "insideLeft", dx:10, fontSize: '8px', fill:COLORS.text, style:{textAnchor:"middle"}}} />
                        <Tooltip formatter={v => [`${v} dB`]} labelFormatter={v => `${Number(v).toFixed(4)} Hz`}
                          contentStyle={{ background: COLORS.panel, border: `1px solid ${COLORS.border}`, fontSize: '9px' }} />
                        <ReferenceLine y={0} stroke="#f59e0b" strokeDasharray="4 2" strokeWidth={1.5}
                          label={{ value: "0 dB", fill: '#f59e0b', fontSize: '8px', position: "right" }} />
                        {/* 이론 교차주파수 fc 기준선 (개루프 0dB) */}
                        {bode_s.bw_theory && (
                          <ReferenceLine x={bode_s.bw_theory} stroke="#f87171" strokeDasharray="2 2" strokeWidth={1.5}
                            label={{ value: `fc:${bode_s.bw_theory}Hz`, fill:'#f87171', fontSize:'7px', position:'insideTopLeft' }} />
                        )}
                        {/* 폐루프 -3dB BW 기준선 */}
                        {bode_s.bw && (
                          <ReferenceLine x={bode_s.bw} stroke="#60a5fa" strokeDasharray="2 2" strokeWidth={1}
                            label={{ value: bode_s.bw >= 1 ? `BW:${Number(bode_s.bw).toFixed(1)}Hz` : `BW:${(bode_s.bw*1000).toFixed(0)}mHz`, fill:'#60a5fa', fontSize:'7px', position:'insideBottomLeft' }} />
                        )}
                        <Line type="monotone" dataKey="mag_dB" stroke={COLORS.accent2} dot={false} strokeWidth={2} name="|L_s(jω)|" />
                        <Line type="monotone" dataKey="T_dB"   stroke="#22c55e"       dot={false} strokeWidth={1.5} strokeDasharray="5 3" name="|T_s(CL)|" />
                        <Legend iconSize={8} wrapperStyle={{ fontSize: '8px' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  {/* 우: 위상 선도 */}
                  <div style={{ background:COLORS.bg, borderRadius:'7px', padding:"8px 10px", border:`1px solid ${COLORS.border}` }}>
                    <div style={{ fontSize:'10px', color:"#a78bfa", fontFamily:mono, marginBottom:'4px', fontWeight:700 }}>
                      📉 {e("위상 선도","Phase Plot")} — ∠L_s(jω) deg
                    </div>
                    <ResponsiveContainer width="100%" height={160}>
                      <LineChart data={bode_s.data} margin={{ top:4, right:6, left:0, bottom:8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} />
                        <XAxis dataKey="freq_hz" scale="log" type="number" domain={bode_s.f_range}
                          tickFormatter={v => v >= 1 ? v.toFixed(1) : v >= 0.01 ? v.toFixed(3) : v.toExponential(1)}
                          tick={{ fontSize: '8px', fill: COLORS.textMuted }}
                          label={{ value: "Hz (log)", position: "insideRight", offset: -2, fontSize: '8px', fill: COLORS.textMuted }} />
                        <YAxis domain={[-270, 0]} ticks={[-270,-180,-90,0]} tick={{ fontSize: '8px', fill: COLORS.textMuted }}
                          label={{ value: "deg", angle: -90, position: "insideLeft", dx:10, fontSize: '8px', fill:COLORS.text, style:{textAnchor:"middle"}}} />
                        <Tooltip formatter={v => [`${v}°`]} labelFormatter={v => `${Number(v).toFixed(4)} Hz`}
                          contentStyle={{ background: COLORS.panel, border: `1px solid ${COLORS.border}`, fontSize: '9px' }} />
                        <ReferenceLine y={-180} stroke="#ef4444" strokeDasharray="4 2" strokeWidth={1.5}
                          label={{ value: "−180°", fill: "#ef4444", fontSize: '8px', position: "right" }} />
                        <ReferenceLine y={-135} stroke="#f59e0b" strokeDasharray="3 3" strokeWidth={1}
                          label={{ value: "−135°(PM=45°)", fill: '#f59e0b', fontSize: '7px', position: "right" }} />
                        {/* 이론 교차주파수 fc 기준선 (개루프 0dB) — 위상여유 읽는 기준점 */}
                        {bode_s.bw_theory && (
                          <ReferenceLine x={bode_s.bw_theory} stroke="#f87171" strokeDasharray="2 2" strokeWidth={1.5}
                            label={{ value: `fc:${bode_s.bw_theory}Hz`, fill:'#f87171', fontSize:'7px', position:'insideTopLeft' }} />
                        )}
                        {bode_s.bw && (
                          <ReferenceLine x={bode_s.bw} stroke="#60a5fa" strokeDasharray="2 2" strokeWidth={1}
                            label={{ value: bode_s.bw >= 1 ? `BW:${Number(bode_s.bw).toFixed(1)}Hz` : `BW:${(bode_s.bw*1000).toFixed(0)}mHz`, fill:'#60a5fa', fontSize:'7px', position:'insideBottomLeft' }} />
                        )}
                        <Line type="monotone" dataKey="phase_deg"   stroke={COLORS.isDark?"#a78bfa":"#5b21b6"} dot={false} strokeWidth={2} name="∠L_s(jω)" />
                        <Legend iconSize={8} wrapperStyle={{ fontSize: '8px' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* 설계 가이드 */}
                <div style={{ marginTop: '8px', padding: '8px', background: COLORS.bg, borderRadius: '6px', border: `1px solid ${COLORS.border}`, fontSize: '9px', color: COLORS.textDim, fontFamily: mono, lineHeight: 1.8 }}>
                  <span style={{ fontWeight: 700, color: COLORS.text }}>{e("⚙️ 루프 분리 설계 가이드","⚙️ Loop Separation Design Guide")}</span>
                  <br/>• {e("속도 루프 BW 권장 상한: 전류 루프 BW ÷ 10","Spd loop BW limit: current loop BW ÷ 10")} = <span style={{ color: '#f59e0b' }}>{bw_spd_rec ? (bw_spd_rec >= 1 ? bw_spd_rec.toFixed(1) : (bw_spd_rec*1000).toFixed(0)+" m")+"Hz" : "N/A"}</span>
                  <br/>• {e("현재 BW","Current BW")}: <span style={{ color: bode_s.bw && bw_spd_rec && bode_s.bw > bw_spd_rec ? "#ef4444" : "#22c55e" }}>{bode_s.bw ? (bode_s.bw >= 1 ? `${bode_s.bw} Hz` : `${(bode_s.bw*1000).toFixed(1)} mHz`) : "N/A"}</span>
                  {bode_s.bw && bw_spd_rec && bode_s.bw > bw_spd_rec && <span style={{ color: "#ef4444" }}> {e("← ⚠ 권장 상한 초과! Kp_spd 낮추기","← ⚠ Exceeds limit! Reduce Kp_spd")}</span>}
                  <br/>• {e("GM=∞ (조건부 안정): 위상이 −180°를 미통과하는 시스템 정상 거동. 단 −180° 여유가 2° 미만이면 파라미터 변동에 취약","GM=∞ (conditionally stable): normal when phase never crosses −180°. Vulnerable if −180° margin < 2°")}
                  <br/>• {e("이득여유 대신","Instead of GM,")} <span style={{ color: COLORS.accent2 }}>{e("위상여유(PM) ≥ 45°","Phase Margin (PM) ≥ 45°")}</span> {e("와","and")} <span style={{ color: COLORS.accent2 }}>−180° {e("여유 ≥ 5°","margin ≥ 5°")}</span> {e("를 핵심 지표로 사용","as key metrics")}
                  <br/>• <span style={{color:"#f87171"}}>fc (빨강 점선)</span>: {e("개루프 0dB 교차주파수 — 위상여유를 이 점에서 읽음","Open-loop 0dB crossover — read PM at this point")} | <span style={{color:"#60a5fa"}}>BW (파랑 점선)</span>: {e("폐루프 −3dB 대역폭","Closed-loop −3dB bandwidth")}
                </div>
              </div>

              {/* [A17] WLTP/FTP 드라이버 PI → sig_input 탭으로 최종 이동 */}
              <div style={{ marginTop:'14px', padding:"8px 12px", borderRadius:'6px',
                background:`${COLORS.accent2}08`, border:`1px solid ${COLORS.accent2}20`,
                fontSize:'9px', color:COLORS.textMuted }}>
                💡 {e("WLTP/FTP 드라이버 PI 설정","WLTP/FTP Driver PI settings")} →&nbsp;
                <button onClick={()=>{setGroup("drive");setSubTab("sig_input");}}
                  style={{color:"#64748b",background:"transparent",border:"none",
                    cursor:"pointer",fontSize:'9px',padding:"0 4px",textDecoration:"underline",fontWeight:700}}>
                  🧑‍✈️ {e("운전자 입력 / 신호 처리","Driver Input / Signal")}
                </button>
              </div>
            </div>
            );
          })()}

          {subTab === "fw_mtpa" && (
            <div>
              <ModalSectionTitle icon="⚡" title={e("약계자 / MTPA 전략 — Field Weakening & MTPA","Field Weakening & MTPA Strategy")} color={COLORS.accent5} />
              <div style={{ marginBottom:'8px', padding:"5px 12px", borderRadius:'5px', fontSize:'9px',
                background:"#16a34a12", border:"1px solid #16a34a40", display:"flex", gap:'14px', flexWrap:"wrap" }}>
                <span style={{ color:"#4ade80", fontWeight:700 }}>🔗 {e("V_dc·I_max_rms → FW 전압한계 직결","V_dc·I_max_rms → FW voltage limit wired")}</span>
                <span style={{ color:"#a78bfa", fontFamily:"monospace", fontSize:'8px' }}>V_max = V_dc×fw_margin/√3 · I_sat = I_max_rms</span>
              </div>
              <div style={{ marginBottom:'10px', padding:'10px', background:`${COLORS.accent5}08`,
                borderRadius:'6px', border:`1px solid ${COLORS.accent5}20`,
                fontSize:9.5, color:COLORS.textDim, fontFamily:mono, lineHeight:1.7 }}>
                <div style={{ fontWeight:700, color:COLORS.accent5, marginBottom:'4px' }}>
                  📐 {e("3단계 전류 지령 선택 로직","3-stage current reference selection")}
                </div>
                <span style={{ color:"#22c55e",fontWeight:700 }}>① MTPA</span>
                {" "}{e("(저속·정상 영역)","(low speed, normal region)")}{" "}<br/>
                {"  "}Id* = (λpm − √(λpm² + 8(Lq−Ld)²·Iq²)) / (4·(Lq−Ld))<br/>
                {"  "}{e("→ 동일 토크를 최소 전류로 발생 (돌극비 활용)","→ Max torque per current (uses saliency)")}<br/>
                <span style={{ color:'#f59e0b',fontWeight:700 }}>② MTPV</span>
                {" "}{e("(고속·전압한계 영역)","(high speed, voltage limit)")}{" "}<br/>
                {"  "}전압 타원: (ωe·Ld·Id + ωe·λpm)² + (ωe·Lq·Iq)² = V_max²<br/>
                {"  "}{e("→ 전압 한계 내 최대 토크 접선점 (Id_mtpv 이차방정식)","→ Max torque on voltage ellipse (quadratic for Id_mtpv)")}<br/>
                <span style={{ color:"#ef4444",fontWeight:700 }}>③ FW</span>
                {" "}{e("(MTPV 토크 불충분 시 폴백)","(fallback when MTPV torque insufficient)")}{" "}<br/>
                {"  "}Id_fw = −min(50, I_max×0.3) → {e("단순 약계자","simple field weakening")}
              </div>

              {/* 현재 운전 regime 실시간 표시 */}
              {(() => {
                const omega_e_est = ((params.gear_ratio||9.5) * (params.r_wheel||0.33) > 0)
                  ? 0 : 0; // 정적 표시 — 실제값은 시뮬에서만 알 수 있음
                const V_max_est = (params.V_dc||400) * (params.fw_v_margin||0.95) / Math.sqrt(3);
                const I_max_est = params.I_max_rms || 600;
                const lpm_est   = params.lambda_pm || 0.12;
                const Ld_est    = params.Ld || 0.0003;
                const Lq_est    = params.Lq || 0.0006;
                const regimes = [
                  { id:'mtpa',                   label:e('MTPA','MTPA'),       color:'#22c55e', desc:e('저속·정상','Low spd') },
                  { id:'mtpv',                   label:'MTPV',                 color:'#f59e0b', desc:e('고속·전압한계','High spd') },
                  { id:'field_weakening_simple', label:e('FW (단순)','FW'),    color:'#ef4444', desc:e('전압초과 폴백','VLim fallback') },
                  { id:'current_limited',        label:e('전류한계','I-lim'),  color:'#a78bfa', desc:e('전류 포화','Current sat.') },
                ];
                return (
                  <div style={{ marginBottom:'10px', padding:'7px 10px', borderRadius:'6px',
                    background:COLORS.bg, border:`1px solid ${COLORS.border}`,
                    fontSize:8.5, fontFamily:mono }}>
                    <div style={{ fontWeight:700, color:COLORS.textMuted, marginBottom:'5px' }}>
                      📊 {e('Regime 선택 기준 (정적 파라미터 기반)','Regime selection (static params)')}
                    </div>
                    <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                      {regimes.map(r => (
                        <div key={r.id} style={{ padding:'4px 8px', borderRadius:'5px',
                          background:`${r.color}15`, border:`1px solid ${r.color}40`,
                          display:'flex', flexDirection:'column', gap:'1px' }}>
                          <span style={{ fontWeight:700, color:r.color }}>{r.label}</span>
                          <span style={{ color:COLORS.textMuted, fontSize:7.5 }}>{r.desc}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop:'5px', color:COLORS.textMuted, fontSize:7.5, lineHeight:1.6 }}>
                      V_max = {((params.V_dc||400)*(params.fw_v_margin||0.95)/Math.sqrt(3)).toFixed(1)} V
                      {" · "}I_max = {params.I_max_rms||600} A
                      {" · "}λpm = {(params.lambda_pm||0.12).toFixed(3)} Wb
                      {" · "}Ld = {((params.Ld||0.0003)*1000).toFixed(2)} mH
                      {" · "}Lq = {((params.Lq||0.0006)*1000).toFixed(2)} mH
                    </div>
                  </div>
                );
              })()}
              <div style={{ display: "grid", minWidth: 0, gridTemplateColumns: GRID.auto2, gap: "0 24px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: '6px', marginBottom: '6px' }}>
                  <label style={{ color: COLORS.textDim, fontSize: '11px', width: 130, fontFamily: mono }}>{e("MTPA 활성화","Enable MTPA")}</label>
                  <button onClick={() => up("mtpa_enable", !params.mtpa_enable)}
                    style={{ padding: "4px 16px", borderRadius: '4px', cursor: "pointer", fontSize: '11px', fontWeight: 700,
                      background: params.mtpa_enable ? COLORS.accent3 : COLORS.bg,
                      color: params.mtpa_enable ? "#000" : COLORS.textDim,
                      border: `1px solid ${params.mtpa_enable ? COLORS.accent3 : COLORS.border}` }}>
                    {params.mtpa_enable ? "ON" : "OFF"}
                  </button>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: '6px', marginBottom: '6px' }}>
                  <label style={{ color: COLORS.textDim, fontSize: '11px', width: 130, fontFamily: mono }}>{e("약계자 (FW) 활성화","Enable FW")}</label>
                  <button onClick={() => up("fw_enable", !params.fw_enable)}
                    style={{ padding: "4px 16px", borderRadius: '4px', cursor: "pointer", fontSize: '11px', fontWeight: 700,
                      background: params.fw_enable ? COLORS.accent3 : COLORS.bg,
                      color: params.fw_enable ? "#000" : COLORS.textDim,
                      border: `1px solid ${params.fw_enable ? COLORS.accent3 : COLORS.border}` }}>
                    {params.fw_enable ? "ON" : "OFF"}
                  </button>
                </div>
                <PI label={e("FW 전압 마진","FW Voltage Margin")} value={params.fw_v_margin} onChange={v => up("fw_v_margin", v)} unit="" step={0.01} min={0.8} max={1.0}
                  desc={e("V_max = V_dc×margin/√3. 낮을수록 고속 여유↑ 효율↓","V_max=V_dc×margin/√3. Lower→more high-spd headroom")} />
                <PI label={e("Id 기본값 (Id_ref)","Id Base (Id_ref)")} value={params.Id_ref_base} onChange={v => up("Id_ref_base", v)} unit="A" step={5} min={-300} max={0} />
              </div>

              {/* MTPV 전용 토글 — 현재 탭에서 누락되어 있던 항목 */}
              <div style={{ marginTop:'10px', padding:'8px 12px', borderRadius:'7px',
                background:`${COLORS.accent5}08`, border:`1px solid ${COLORS.accent5}30` }}>
                <div style={{ fontSize:'10px', fontWeight:700, color:COLORS.accent5, marginBottom:'6px' }}>
                  ⚡ {e('MTPV 제어 설정','MTPV Control')}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:GRID.auto2, gap:'0 24px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'6px' }}>
                    <label style={{ color:COLORS.textDim, fontSize:'11px', width:130, fontFamily:mono }}>
                      {e('MTPV 활성화','Enable MTPV')}
                    </label>
                    <button onClick={() => up('mtpv_enable', params.mtpv_enable===false ? true : false)}
                      style={{ padding:'4px 16px', borderRadius:'4px', cursor:'pointer', fontSize:'11px', fontWeight:700,
                        background: params.mtpv_enable!==false ? '#f59e0b' : COLORS.bg,
                        color: params.mtpv_enable!==false ? '#000' : COLORS.textDim,
                        border: `1px solid ${params.mtpv_enable!==false ? '#f59e0b' : COLORS.border}` }}>
                      {params.mtpv_enable!==false ? 'ON' : 'OFF'}
                    </button>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'6px' }}>
                    <label style={{ color:COLORS.textDim, fontSize:'11px', width:130, fontFamily:mono }}>
                      {e('과변조 (Overmod)','Overmodulation')}
                    </label>
                    <button onClick={() => up('overmod_enable', !params.overmod_enable)}
                      style={{ padding:'4px 16px', borderRadius:'4px', cursor:'pointer', fontSize:'11px', fontWeight:700,
                        background: params.overmod_enable ? COLORS.accent : COLORS.bg,
                        color: params.overmod_enable ? '#000' : COLORS.textDim,
                        border: `1px solid ${params.overmod_enable ? COLORS.accent : COLORS.border}` }}>
                      {params.overmod_enable ? 'ON' : 'OFF'}
                    </button>
                  </div>
                </div>
                <div style={{ fontSize:'8px', color:COLORS.textMuted, fontFamily:mono, marginTop:'2px', lineHeight:1.6 }}>
                  {e('MTPV는 fw_enable이 ON일 때만 동작합니다. 전압 타원 이차방정식으로 Id_mtpv를 계산하며, MTPV 토크가 요구 토크의 95% 미만이면 FW(단순 약계자)로 폴백합니다.',
                     'MTPV only active when fw_enable=ON. Solves quadratic for Id_mtpv on voltage ellipse. Falls back to simple FW if MTPV torque < 95% of required torque.')}
                </div>
              </div>

              {/* 전기 파라미터 참조 패널 (읽기 전용) */}
              <div style={{ marginTop:'10px', padding:'8px 12px', borderRadius:'7px',
                background:COLORS.bg, border:`1px solid ${COLORS.border}` }}>
                <div style={{ fontSize:9.5, fontWeight:700, color:COLORS.textMuted, marginBottom:'6px' }}>
                  🔍 {e('MTPA/MTPV/FW 관련 전기 파라미터 (참조용 — 값 변경은 모터 전기 탭에서)','MTPA/MTPV/FW electric params (read-only — edit in motor electric tab)')}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(min(160px,43vw),1fr))', gap:'4px' }}>
                  {[
                    { k:'λpm',      v:`${(params.lambda_pm||0.12).toFixed(4)} Wb`,  color:'#a78bfa',
                      desc:e('영구자석 자속 — MTPA Id*/MTPV 전압 타원 핵심','PM flux — key for MTPA Id* & MTPV ellipse') },
                    { k:'k_lambda', v:`${(params.k_lambda||0.001).toFixed(4)} /°C`, color:'#a78bfa',
                      desc:e('자속 온도계수 λpm(T)=λpm×(1−k×ΔT)','Flux temp coeff λpm(T)=λpm×(1−k×ΔT)') },
                    { k:'Ld',       v:`${((params.Ld||0.0003)*1000).toFixed(3)} mH`, color:'#60a5fa',
                      desc:e('d축 인덕턴스 — MTPV 이차방정식 a계수','d-axis inductance — MTPV quadratic a') },
                    { k:'Lq',       v:`${((params.Lq||0.0006)*1000).toFixed(3)} mH`, color:'#60a5fa',
                      desc:e('q축 인덕턴스 — MTPA 돌극비 계산','q-axis inductance — MTPA saliency') },
                    { k:'I_max_rms',v:`${params.I_max_rms||600} A`,                 color:'#22c55e',
                      desc:e('전류원 반지름 — MTPV 전류 포화 한계','Current circle radius — MTPV saturation limit') },
                    { k:'T_ref',    v:`${params.T_ref||25} °C`,                     color:'#f59e0b',
                      desc:e('온도 보정 기준점','Temperature reference for derating') },
                  ].map(({k,v,color,desc}) => (
                    <div key={k} style={{ padding:'5px 8px', borderRadius:'5px',
                      background:`${color}0d`, border:`1px solid ${color}30` }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <span style={{ fontSize:'9px',fontWeight:700,color,fontFamily:mono }}>{k}</span>
                        <span style={{ fontSize:'9px',fontWeight:700,color }}>{v}</span>
                      </div>
                      <div style={{ fontSize:'7px',color:COLORS.textMuted,marginTop:'1px',lineHeight:1.3 }}>{desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── [D3] 자기 포화 인덕턴스 맵 (saturatedInductance) ── */}
              <div style={{ marginTop: '14px', padding: '10px 14px', borderRadius: '8px',
                background: '#a78bfa08', border: '1px solid #a78bfa30' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: '#a78bfa', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  🧲 {e('자기 포화 인덕턴스 맵 — Ld(Id) · Lq(Iq)', 'Magnetic Saturation Map — Ld(Id) · Lq(Iq)')}
                  <span style={{ fontSize: '8px', fontWeight: 400, color: COLORS.textMuted, fontFamily: mono }}>
                    saturatedInductance()
                  </span>
                </div>

                {/* ks_d / ks_q 파라미터 인라인 슬라이더 */}
                <div style={{ display: 'grid', gridTemplateColumns: GRID.auto2, gap: '0 24px', marginBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                    <label style={{ color: COLORS.textDim, fontSize: '11px', width: 140, fontFamily: mono }}>
                      {e('자기 포화 활성화', 'Enable Mag. Saturation')}
                    </label>
                    <button onClick={() => up('magnetic_saturation_enable', !params.magnetic_saturation_enable)}
                      style={{ padding: '4px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 700,
                        background: params.magnetic_saturation_enable ? '#a78bfa' : COLORS.bg,
                        color: params.magnetic_saturation_enable ? '#fff' : COLORS.textDim,
                        border: `1px solid ${params.magnetic_saturation_enable ? '#a78bfa' : COLORS.border}` }}>
                      {params.magnetic_saturation_enable ? 'ON' : 'OFF'}
                    </button>
                  </div>
                  <PI label={e('d축 포화 계수 ks_d', 'ks_d (d-axis sat.)')}
                    value={params.ks_d ?? 0.20} onChange={v => up('ks_d', v)}
                    unit="" step={0.05} min={0} max={1.0}
                    desc={e('Ld(Id) = Ld0/(1+ks_d·|Id|/I_rated)  — 0 = 포화 없음', 'Ld(Id)=Ld0/(1+ks_d·|Id|/I_rated) — 0=no sat')} />
                  <PI label={e('q축 포화 계수 ks_q', 'ks_q (q-axis sat.)')}
                    value={params.ks_q ?? 0.40} onChange={v => up('ks_q', v)}
                    unit="" step={0.05} min={0} max={1.0}
                    desc={e('Lq(Iq) = Lq0/(1+ks_q·|Iq|/I_rated)  — q축 더 빠르게 포화', 'Lq(Iq)=Lq0/(1+ks_q·|Iq|/I_rated) — q saturates faster')} />
                </div>

                {/* 포화 곡선 차트 */}
                {(() => {
                  const I_rated = params.I_max_rms || 600;
                  const steps   = 25;
                  const satData = Array.from({ length: steps }, (_, i) => {
                    const I = (i / (steps - 1)) * I_rated;
                    // d축: Id = -I (음수 방향 약계자), Iq = 0
                    const resd = saturatedInductance(-I, 0,   { ...params, magnetic_saturation_enable: true });
                    const resq = saturatedInductance(0,  I,   { ...params, magnetic_saturation_enable: true });
                    const base = saturatedInductance(0,  0,   { ...params, magnetic_saturation_enable: true });
                    return {
                      I_A:      +I.toFixed(0),
                      Ld_mH:    +(resd.Ld * 1000).toFixed(4),
                      Lq_mH:    +(resq.Lq * 1000).toFixed(4),
                      Ld0_mH:   +(base.Ld0 * 1000).toFixed(4),
                      Lq0_mH:   +(base.Lq0 * 1000).toFixed(4),
                      sat_d:    +resd.sat_d.toFixed(4),
                      sat_q:    +resq.sat_q.toFixed(4),
                    };
                  });
                  // ks=0 (비포화) 참조선
                  const Ld0_ref = ((params.Ld || 0.0003) * 1000).toFixed(4);
                  const Lq0_ref = ((params.Lq || 0.0006) * 1000).toFixed(4);
                  return (
                    <div key={colLayout} style={{ display: 'grid', gridTemplateColumns:_gc, gap: '10px' }}>
                      {/* Ld(Id) / Lq(Iq) 포화 곡선 */}
                      <div>
                        <div style={{ fontSize: '9px', color: COLORS.textMuted, marginBottom: '4px', fontFamily: mono }}>
                          Ld(|Id|) · Lq(|Iq|) {e('vs 전류 진폭', 'vs current magnitude')}
                        </div>
                        <ResponsiveContainer width="100%" height={200}>
                          <ComposedChart data={satData} margin={{ top: 4, right: 10, bottom: 16, left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="I_A" tick={{ fontSize: '9px' }}
                              label={{ value: 'I [A]', position: 'insideBottom', offset: -4, fontSize: '10px' }} />
                            <YAxis tick={{ fontSize: '9px' }}
                              label={{ value: 'L [mH]', angle: -90, position: 'insideLeft', fontSize: '10px' }} />
                            <Tooltip formatter={(v, n) => [parseFloat(v).toFixed(4) + ' mH', n]} />
                            <Legend iconSize={8} wrapperStyle={{ fontSize: '10px' }} />
                            <ReferenceLine y={parseFloat(Ld0_ref)} stroke="#60a5fa" strokeDasharray="4 2"
                              label={{ value: `Ld0=${Ld0_ref}`, fill: '#60a5fa', fontSize: '8px', position: 'right' }} />
                            <ReferenceLine y={parseFloat(Lq0_ref)} stroke="#a78bfa" strokeDasharray="4 2"
                              label={{ value: `Lq0=${Lq0_ref}`, fill: '#a78bfa', fontSize: '8px', position: 'right' }} />
                            <Line type="monotone" dataKey="Ld_mH" name="Ld(Id) mH"
                              stroke="#60a5fa" strokeWidth={2} dot={false}
                              strokeDasharray={params.magnetic_saturation_enable ? undefined : '5 3'} />
                            <Line type="monotone" dataKey="Lq_mH" name="Lq(Iq) mH"
                              stroke="#a78bfa" strokeWidth={2} dot={false}
                              strokeDasharray={params.magnetic_saturation_enable ? undefined : '5 3'} />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                      {/* 포화 계수 sat_d / sat_q */}
                      <div>
                        <div style={{ fontSize: '9px', color: COLORS.textMuted, marginBottom: '4px', fontFamily: mono }}>
                          sat_d · sat_q = L/L0 {e('(1.0 = 미포화)', '(1.0 = unsaturated)')}
                        </div>
                        <ResponsiveContainer width="100%" height={200}>
                          <LineChart data={satData} margin={{ top: 4, right: 10, bottom: 16, left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="I_A" tick={{ fontSize: '9px' }}
                              label={{ value: 'I [A]', position: 'insideBottom', offset: -4, fontSize: '10px' }} />
                            <YAxis domain={[0, 1.05]} tick={{ fontSize: '9px' }}
                              label={{ value: 'sat ratio', angle: -90, position: 'insideLeft', fontSize: '10px' }} />
                            <Tooltip formatter={(v, n) => [(v * 100).toFixed(1) + '%', n]} />
                            <Legend iconSize={8} wrapperStyle={{ fontSize: '10px' }} />
                            <ReferenceLine y={1.0} stroke="#6b7280" strokeDasharray="3 2" />
                            <Line type="monotone" dataKey="sat_d" name={e('d축 인덕턴스 포화율','D-axis Inductance Saturation Ratio')}
                              stroke="#60a5fa" strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="sat_q" name={e('q축 인덕턴스 포화율','Q-axis Inductance Saturation Ratio')}
                              stroke="#a78bfa" strokeWidth={2} dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  );
                })()}

                <div style={{ marginTop: '6px', fontSize: '8px', color: COLORS.textMuted, fontFamily: mono, lineHeight: 1.6 }}>
                  {e(
                    'magnetic_saturation_enable=false → Ld/Lq 고정 (점선). ON 시 ks_d·ks_q에 따라 MTPA Id* 정밀도 향상. ' +
                    'Ldq 교차결합 항 포함 (ks_dq 기반). 파라미터는 모터 전기 탭에서 Ld·Lq 직접 수정 가능.',
                    'magnetic_saturation_enable=false → Ld/Lq fixed (dashed). ON: ks_d·ks_q improve MTPA Id* accuracy. ' +
                    'Cross-coupling Ldq term included. Edit Ld·Lq in motor-electric tab.'
                  )}
                </div>
              </div>
            </div>
          )}

          {subTab === "pwm" && (
            <div>
              {/* PWM 구현 방식 배너 */}
              <div style={{ marginBottom:'8px', padding:"7px 12px", borderRadius:'5px', fontSize:'9px',
                background:"#ec489910", border:"1px solid #ec489940", lineHeight:1.7 }}>
                <div style={{ color:"#ec4899", fontWeight:700, marginBottom:'3px' }}>
                  ⚡ {e("SVPWM 변조 기반 전압 지령","SVPWM-based Voltage Modulation")}
                </div>
                <div style={{ color:COLORS.textMuted, fontFamily:mono, fontSize:'8px' }}>
                  {e("역Park(dq→αβ) → 역Clarke(αβ→3상) → 섹터 판별 → T1/T2/T0 → DPWM/Overmod 분기",
                     "Inv. Park(dq→αβ) → Inv. Clarke(αβ→3ph) → Sector → T1/T2/T0 → DPWM/Overmod")}<br/>
                  V_linear = Vdc/√3 = {((params.V_dc||400)/Math.sqrt(3)).toFixed(1)} V
                  {" | "}M_sixstep = 2√3/π ≈ {(2*Math.sqrt(3)/Math.PI).toFixed(4)}
                  {" | "}V_6step(기본파) = {((params.V_dc||400)*2/Math.PI).toFixed(1)} V<br/>
                  🔗 DPWM: m_a ≥ {params.dpwm_m_threshold??0.5} 에서 활성 (저속 SVPWM 폴백 보호)
                  {" | "}P_sw = 6·E_on_off·f_sw·(I/300)·(Vdc/400)
                </div>
              </div>
              {/* PWM 모드 선택 */}
              <div style={{ marginBottom:'10px', padding:"7px 10px", borderRadius:'7px',
                background:`${"#ec4899"}08`, border:`1px solid ${"#ec4899"}30` }}>
                <div style={{ fontSize:9.5, fontWeight:700, color:"#ec4899", marginBottom:'6px' }}>
                  📶 {e("변조 방식 선택","Modulation Mode")}
                </div>
                <div style={{ display:"flex", gap:'5px', flexWrap:"wrap", marginBottom:'8px' }}>
                  {[
                    { id:"SVPWM",    label:"SVPWM",    desc:e("기본 — T0 균등 배분","Standard — T0 symmetric"), color:"#ec4899" },
                    { id:"DPWM1",    label:"DPWM1",    desc:e("고효율 — 양 클램핑 (손실 ↓33%)","High eff — pos clamp (loss ↓33%)"), color:'#f59e0b' },
                    { id:"DPWM0",    label:"DPWM0",    desc:e("고효율 — 음 클램핑","High eff — neg clamp"), color:'#f59e0b' },
                    { id:"DPWM_MAX", label:"DPWM_MAX", desc:e("손실 최소 자동선택","Auto min-loss select"), color:"#22c55e" },
                  ].map(m => {
                    const active = (params.pwm_mode||"SVPWM") === m.id;
                    return (
                      <button key={m.id} onClick={() => up("pwm_mode", m.id)}
                        style={{ padding:"5px 12px", borderRadius:'6px', cursor:"pointer",
                          fontSize:9.5, fontWeight: active?700:500,
                          background: active?`${m.color}25`:COLORS.bg,
                          color: active?m.color:COLORS.textMuted,
                          border:`1.5px solid ${active?m.color:COLORS.border}`,
                          display:"flex", flexDirection:"column", alignItems:"center", gap:'1px',
                          boxShadow: active?`0 0 6px ${m.color}40`:"none",
                          transition:"all 0.15s" }}>
                        <span>{m.id}</span>
                        <span style={{ fontSize:'7px', opacity:0.8 }}>{m.desc}</span>
                      </button>
                    );
                  })}
                </div>
                {/* ── PWM 실시간 상태 패널 ─────────────────────────────── */}
                {(() => {
                  const Vdc        = params.V_dc || 400;
                  const sqrt3      = Math.sqrt(3);
                  const V_max_lin  = Vdc / sqrt3;
                  const V_max_6st  = Vdc * 2 / Math.PI;
                  const M_SIXSTEP  = V_max_6st / V_max_lin;
                  const dpwm_thr   = params.dpwm_m_threshold ?? 0.5;
                  const pwm_mode   = params.pwm_mode || "SVPWM";
                  const isDPWM     = pwm_mode.startsWith("DPWM");
                  const overmod_en = params.overmod_enable || false;

                  const omega_ref   = (params.P_max_wheel||220000) / (params.T_max_motor||400);
                  const omega_e_ref = Math.min(omega_ref, 800) * (params.poles||8) / 2;
                  const lpm_ref     = params.lambda_pm || 0.12;
                  const Id_ref_est  = params.Id_ref_base || 0;
                  const Iq_ref_est  = (params.T_max_motor||400) * 0.5 /
                    (1.5 * (params.poles||8)/2 * lpm_ref);
                  const Vd_est  = -omega_e_ref * (params.Lq||0.0006) * Iq_ref_est;
                  const Vq_est  =  omega_e_ref * ((params.Ld||0.0003)*Id_ref_est + lpm_ref);

                  let svpwm_live = null;
                  try {
                    svpwm_live = svpwmModulate(Vd_est, Vq_est, 0.52, Vdc, {
                      pwm_mode, overmod_enable: overmod_en,
                      dpwm_m_threshold: dpwm_thr, Id: Id_ref_est,
                    });
                  } catch(err) { /* fallback */ }

                  const m_a_live    = svpwm_live ? svpwm_live.m_a_raw : 0;
                  const mode_active = svpwm_live ? svpwm_live.pwm_mode_active : pwm_mode;
                  const dpwm_on     = svpwm_live ? svpwm_live.dpwm_active     : false;
                  const sixstep_on  = svpwm_live ? svpwm_live.is_sixstep      : false;

                  const modeColor = sixstep_on ? "#ef4444"
                    : mode_active === "OVERMOD" ? "#f97316"
                    : dpwm_on ? "#f59e0b" : "#ec4899";

                  const gaugeMax = M_SIXSTEP + 0.15;
                  const gaugePct = Math.min(m_a_live / gaugeMax * 100, 100);
                  const thr_pct  = dpwm_thr  / gaugeMax * 100;
                  const lin_pct  = 1.0        / gaugeMax * 100;
                  const six_pct  = M_SIXSTEP  / gaugeMax * 100;

                  return (
                    <div style={{ marginTop:'8px' }}>
                      {/* 모드 배지 + 수치 */}
                      <div style={{ display:"flex", alignItems:"center", gap:'6px', marginBottom:'6px', flexWrap:"wrap" }}>
                        <span style={{ fontSize:'8px', padding:"2px 7px", borderRadius:'10px',
                          background:`${modeColor}22`, color:modeColor,
                          fontWeight:700, fontFamily:mono, border:`1px solid ${modeColor}55` }}>
                          {mode_active}
                        </span>
                        <span style={{ fontSize:'8px', fontFamily:mono, color:COLORS.textMuted }}>
                          m_a = <span style={{color:modeColor,fontWeight:700}}>{m_a_live.toFixed(3)}</span>
                          {" | "}
                          M_6ST = <span style={{color:'#f59e0b',fontWeight:700}}>{M_SIXSTEP.toFixed(4)}</span>
                        </span>
                        {dpwm_on && (
                          <span style={{ fontSize:'7px', padding:"1px 5px", borderRadius:'8px',
                            background:"#f59e0b22", color:'#f59e0b', border:"1px solid #f59e0b44" }}>
                            {e("DPWM 활성 — 손실 ↓33%","DPWM active — loss ↓33%")}
                          </span>
                        )}
                        {isDPWM && !dpwm_on && m_a_live < dpwm_thr && (
                          <span style={{ fontSize:'7px', padding:"1px 5px", borderRadius:'8px',
                            background:"#6b728022", color:COLORS.textMuted, border:"1px solid #6b728044" }}>
                            {e("SVPWM 폴백 (m_a < 임계값)","SVPWM fallback (m_a < thr)")}
                          </span>
                        )}
                        {sixstep_on && (
                          <span style={{ fontSize:'7px', padding:"1px 5px", borderRadius:'8px',
                            background:"#ef444422", color:"#ef4444", border:"1px solid #ef444444" }}>
                            {e("6-step 완전 전환","Full 6-step mode")}
                          </span>
                        )}
                      </div>

                      {/* m_a 게이지 바 */}
                      <div style={{ position:"relative", height:14, borderRadius:'4px',
                        background:COLORS.border+"44", overflow:"visible", marginBottom:'4px' }}>
                        <div style={{
                          position:"absolute", left:0, top:0, height:"100%",
                          width:`${gaugePct}%`, borderRadius:'4px',
                          background:`linear-gradient(90deg, #ec489988 0%, ${modeColor} 100%)`,
                          transition:"width 0.3s ease",
                        }}/>
                        {isDPWM && (
                          <div style={{ position:"absolute", left:`${thr_pct}%`, top:-3,
                            bottom:-3, width:1.5, background:'#f59e0b', borderRadius:'1px' }}/>
                        )}
                        <div style={{ position:"absolute", left:`${lin_pct}%`, top:-3,
                          bottom:-3, width:1.5, background:"#ec4899", borderRadius:'1px' }}/>
                        <div style={{ position:"absolute", left:`${six_pct}%`, top:-3,
                          bottom:-3, width:1.5, background:"#ef4444", borderRadius:'1px' }}/>
                      </div>

                      {/* 눈금 레이블 */}
                      <div style={{ position:"relative", height:10, fontSize:'7px',
                        fontFamily:mono, color:COLORS.textMuted }}>
                        {isDPWM && (
                          <span style={{ position:"absolute", left:`${thr_pct}%`,
                            transform:"translateX(-50%)", color:'#f59e0b' }}>
                            {dpwm_thr.toFixed(2)}
                          </span>
                        )}
                        <span style={{ position:"absolute", left:`${lin_pct}%`,
                          transform:"translateX(-50%)", color:"#ec4899" }}>1.00</span>
                        <span style={{ position:"absolute", left:`${six_pct}%`,
                          transform:"translateX(-50%)", color:"#ef4444" }}>
                          {M_SIXSTEP.toFixed(3)}
                        </span>
                      </div>

                      {/* 구간 설명 */}
                      <div style={{ fontSize:7.5, fontFamily:mono, color:COLORS.textMuted,
                        display:"flex", gap:"2px 12px", flexWrap:"wrap", marginTop:'3px' }}>
                        <span style={{color:"#ec4899"}}>■</span>
                        <span>{e("≤1.0: SVPWM 선형","≤1.0: SVPWM linear")}</span>
                        {overmod_en && <>
                          <span style={{color:"#f97316"}}>■</span>
                          <span>{e("1.0~1.103: Overmod I (hexagon 클램프)","1.0~1.103: Overmod I (hexagon clamp)")}</span>
                          <span style={{color:"#ef4444"}}>■</span>
                          <span>{e(">1.103: 6-step (섹터별 고정 duty)",">1.103: 6-step (fixed sector duty)")}</span>
                        </>}
                        {isDPWM && <>
                          <span style={{color:'#f59e0b'}}>▏</span>
                          <span>{e(`m_a≥${dpwm_thr.toFixed(2)}: DPWM 활성`,`m_a≥${dpwm_thr.toFixed(2)}: DPWM active`)}</span>
                        </>}
                      </div>

                      {/* 수치 요약 */}
                      <div style={{ fontSize:7.5, fontFamily:mono, color:COLORS.textMuted,
                        display:"flex", gap:"2px 14px", flexWrap:"wrap", marginTop:'4px' }}>
                        <span>V_linear=<span style={{color:"#ec4899"}}>{V_max_lin.toFixed(1)}V</span></span>
                        <span>V_6step=<span style={{color:'#f59e0b'}}>{V_max_6st.toFixed(1)}V</span></span>
                        {svpwm_live && <>
                          <span>sector=<span style={{color:modeColor}}>{svpwm_live.sector}</span></span>
                          <span>T0=<span style={{color:COLORS.textDim}}>{svpwm_live.T0.toFixed(3)}</span></span>
                          <span>duty=[<span style={{color:modeColor}}>
                            {svpwm_live.duty_A.toFixed(2)},{svpwm_live.duty_B.toFixed(2)},{svpwm_live.duty_C.toFixed(2)}
                          </span>]</span>
                        </>}
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div style={{ display: "grid", minWidth: 0, gridTemplateColumns: GRID.auto2, gap: "0 24px" }}>
                <PI label={e("캐리어 주파수","Carrier Frequency")} value={params.carrier_freq} onChange={v => up("carrier_freq", v)} unit="Hz" step={1000} min={2000} max={30000}
                  desc={e("전류 리플 ↔ 스위칭 손실 트레이드오프","Current ripple ↔ switching loss trade-off")} />
                <PI label={e("데드타임","Dead Time")} value={params.dead_time * 1e6} onChange={v => up("dead_time", v / 1e6)} unit="μs" step={0.5} min={0.5} max={10}
                  desc={e("SiC: 0.5~1μs | Si-IGBT: 2~4μs","SiC: 0.5~1μs | Si-IGBT: 2~4μs")} />
                <PI label={e("제어 주기 (ctrl_dt)","Control Period (ctrl_dt)")} value={params.ctrl_dt * 1e6} onChange={v => up("ctrl_dt", v / 1e6)} unit="μs" step={10} min={10} max={1000}
                  desc={e("1/carrier_freq의 정수배 권장","Recommend integer multiple of 1/carrier_freq")} />
                <PI label={e("DPWM 전환 m_a 하한","DPWM m_a Threshold")} value={params.dpwm_m_threshold??0.5} onChange={v => up("dpwm_m_threshold", v)} unit="" step={0.05} min={0.2} max={1.0}
                  desc={e("이 값 이상일 때 DPWM 적용 (저속 경부하 제외)","Apply DPWM when m_a exceeds threshold")} />
              </div>

              {/* [A12] 인버터 지연 & 데드타임 보상 — traction_lut에서 이동 + 신규 추가 */}
              <div style={{ marginTop:'14px', padding:'10px', borderRadius:'8px', border:"1px solid #ec489940", background:"#ec489908" }}>
                <div style={{ display:"flex", alignItems:"center", gap:'10px', marginBottom:'8px', flexWrap:"wrap" }}>
                  <div style={{ fontSize:'11px', fontWeight:700, color:"#ec4899" }}>
                    ⏱ {e("인버터 지연 & 하드웨어 특성","Inverter Delay & Hardware Characteristics")}
                  </div>
                  {/* [Fix-M1] deadtime_compensation_enable 토글 */}
                  <button onClick={()=>up("deadtime_compensation_enable",!(params.deadtime_compensation_enable??false))}
                    style={{ padding:"2px 10px", borderRadius:'4px', border:"none", cursor:"pointer",
                      fontSize:9.5, fontWeight:700,
                      background:(params.deadtime_compensation_enable??false)?"#ec4899":COLORS.border,
                      color:(params.deadtime_compensation_enable??false)?"#fff":COLORS.textMuted }}>
                    DT Comp: {(params.deadtime_compensation_enable??false)?"ON":"OFF"}
                  </button>
                  {!(params.deadtime_compensation_enable??false) && (
                    <span style={{fontSize:8.5,color:"#f87171"}}>
                      ⚠ {e("보상 비활성 — gain/threshold 설정 무효","Comp. disabled — gain/threshold ignored")}
                    </span>
                  )}
                </div>
                <div style={{ display:"grid", gridTemplateColumns:GRID.auto2, gap:"0 24px" }}>
                  <PI label={e("인버터 지연","Inverter Delay")} value={params.inverter_delay_ms??1.0}
                    onChange={v=>up("inverter_delay_ms",v)} unit="ms" step={0.5} min={0.5} max={5}
                    desc={e("SiC: 0.5~1ms | Si-IGBT: 1~3ms","SiC: 0.5~1ms | Si-IGBT: 1~3ms")} />
                  <PI label={e("전류루프 BW","Current Loop BW")} value={params.current_loop_bw_hz??1000}
                    onChange={v=>up("current_loop_bw_hz",v)} unit="Hz" step={100} min={200} max={3000}
                    desc={e("SiC 일반: 1~3kHz | Si-IGBT: 300~800Hz","SiC typical: 1~3kHz | Si-IGBT: 300~800Hz")} />
                  <PI label={e("데드타임 보상 계수","Dead-Time Comp. Gain")} value={params.dead_time_comp_gain??1.0}
                    onChange={v=>up("dead_time_comp_gain",v)} unit="×" step={0.05} min={0} max={2.0}
                    desc={e("1.0=완전 보상 | 0=보상 없음 | >1.0=과보상","1.0=full comp | 0=none | >1=over-comp")} />
                  <PI label={e("데드타임 보상 임계 전류","DT Comp. Current Threshold")} value={params.dead_time_comp_i_thr??5}
                    onChange={v=>up("dead_time_comp_i_thr",v)} unit="A" step={1} min={0} max={30}
                    desc={e("이 전류 이하에서 보상 비활성 (영교차 채터링 방지)","Disable compensation below this current (zero-crossing)")} />
                </div>
                <div style={{ marginTop:'8px', padding:"5px 10px", borderRadius:'5px', fontSize:8.5, color:COLORS.textMuted, fontFamily:"monospace", background:COLORS.bg, lineHeight:1.7 }}>
                  V_comp = dead_time·Vdc·f_sw·sign(i) / (1 + |i|/i_thr)<br/>
                  {e("💡 과보상(>1.0)은 전류 리플 증가 원인 — 보통 0.8~1.0 권장","💡 Over-compensation (>1.0) increases current ripple — 0.8~1.0 typical")}
                </div>
              </div>

              {/* ── DC 링크 전압 & 커패시터 다이나믹스 ── */}
              <div style={{ marginTop:'14px', padding:'10px', borderRadius:'8px',
                border:`1px solid #60a5fa40`, background:`#60a5fa08` }}>
                <div style={{ fontSize:'11px', fontWeight:700, color:'#60a5fa', marginBottom:'4px' }}>
                  🔋 {e("DC 링크 전압 / 커패시터 다이나믹스","DC Link Voltage / Capacitor Dynamics")}
                </div>
                <div style={{ fontSize:8.5, color:COLORS.textMuted, marginBottom:'8px', lineHeight:1.6 }}>
                  {e("dc_link_dynamics_enable=ON 시 V_terminal → V_dc_eff 1차 RC 피드백 (2RC 배터리 모델 필요). OFF 시 V_dc 고정.",
                     "When ON: V_terminal → V_dc_eff via 1st-order RC filter (requires 2RC battery model). OFF: V_dc fixed.")}
                </div>
                <div style={{ display:"grid", gridTemplateColumns:GRID.auto2, gap:"0 24px" }}>
                  <PI label={e("DC 링크 정격 전압","DC Link Voltage")} value={params.V_dc??400}
                    onChange={v=>up("V_dc",v)} unit="V" step={10} min={100} max={900}
                    desc={e("인버터 입력 정격 전압. 400V/800V 아키텍처","Inverter input rated voltage. 400V/800V arch")} />
                  <PI label={e("과전압 차단 한계","Overvolt Cutoff")} value={params.V_dc_max??(params.V_dc??400)*1.05}
                    onChange={v=>up("V_dc_max",v)} unit="V" step={5} min={200} max={1000}
                    desc={e("이 전압 초과 시 V_dc_eff 클램프 (회생 과전압 보호)","Clamp V_dc_eff above this (regen overvolt prot.)")} />
                  <PI label={e("저전압 차단 한계","Undervolt Cutoff")} value={params.V_dc_min??250}
                    onChange={v=>up("V_dc_min",v)} unit="V" step={5} min={100} max={500}
                    desc={e("이 전압 이하 시 V_dc_eff 클램프 (방전 종지)","Clamp V_dc_eff below this (discharge cutoff)")} />
                  <PI label={e("DC 링크 커패시터","DC Link Capacitor")} value={(params.C_dc??1500e-6)*1e6}
                    onChange={v=>up("C_dc",v/1e6)} unit="μF" step={100} min={100} max={5000}
                    desc={e("τ=C×R_batt → 전압 새그 응답 속도 결정","τ=C×R_batt → determines voltage sag response speed")} />
                </div>
                <div style={{ marginTop:'8px', display:"flex", alignItems:"center", gap:'8px' }}>
                  <span style={{ fontSize:'9px', color:COLORS.textMuted }}>
                    {e("DC 링크 다이나믹스","DC Link Dynamics")}
                  </span>
                  <button onClick={()=>up("dc_link_dynamics_enable",!(params.dc_link_dynamics_enable??false))}
                    style={{ padding:"3px 12px", borderRadius:'5px', border:"none", cursor:"pointer",
                      fontSize:'10px', fontWeight:700,
                      background: (params.dc_link_dynamics_enable??false) ? "#60a5fa" : COLORS.border,
                      color: (params.dc_link_dynamics_enable??false) ? "#000" : COLORS.textMuted }}>
                    {(params.dc_link_dynamics_enable??false)
                      ? (lang==="en"?"ON — V_dc dynamic":"ON — V_dc 동적")
                      : (lang==="en"?"OFF — V_dc fixed":"OFF — V_dc 고정")}
                  </button>
                  {(params.dc_link_dynamics_enable??false) && params.battery_model !== "2RC" && (
                    <span style={{ fontSize:8.5, color:"#f87171" }}>
                      ⚠ {e("2RC 배터리 모델 필요 (BMS → SOC 추정 탭에서 전환)","Requires 2RC battery model (set in BMS → SOC tab)")}
                    </span>
                  )}
                  {(params.dc_link_dynamics_enable??false) && params.battery_model === "2RC" && (
                    <span style={{ fontSize:8.5, color:"#4ade80" }}>
                      ✓ {e(`τ=${((params.C_dc??1500e-6)*(params.R_batt??0.08)*1000).toFixed(1)}ms`,
                           `τ=${((params.C_dc??1500e-6)*(params.R_batt??0.08)*1000).toFixed(1)}ms`)}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ marginTop: '12px', padding: '10px', background: COLORS.bg, borderRadius: '6px', fontSize: '10px', color: COLORS.textMuted, fontFamily: mono, lineHeight: 1.5 }}>
                {e("💡 Si-IGBT 일반: f_sw 6~10kHz, dead-time 2~4μs | SiC-MOSFET: f_sw 10~20kHz, dead-time 0.5~2μs","💡 Si-IGBT typical: f_sw 6~10kHz, dead-time 2~4μs | SiC-MOSFET: f_sw 10~20kHz, dead-time 0.5~2μs")}<br/>
                {e("💡 캐리어 주파수↑ → 전류 리플↓, 음향 개선 / 스위칭 손실↑ → 효율과 NVH 트레이드오프","💡 Higher carrier freq → less current ripple, better acoustics / more switching loss → efficiency/NVH trade-off")}
              </div>

              {/* ── [Fix-M5] 인버터 손실 모델 계수 ── */}
              <div style={{ marginTop:'14px', padding:"10px 14px", borderRadius:'8px',
                border:"1px solid #ec489940", background:"#ec489904" }}>
                <div style={{ fontSize:'11px', fontWeight:700, color:"#ec4899", marginBottom:'6px' }}>
                  📉 {e("인버터 손실 모델 계수","Inverter Loss Model Coefficients")}
                </div>
                <div style={{ fontSize:'9px', color:COLORS.textMuted, marginBottom:'8px', lineHeight:1.7,
                  fontFamily:"monospace", padding:"5px 8px", borderRadius:'4px', background:COLORS.bg }}>
                  P_sw = 6·E_on_off·f_sw·(I/300)·(Vdc/400)<br/>
                  P_cond = 6·(V_f_diode·I·0.45 + R_sw·I²)   P_fe = (k_h·f·B¹·⁶ + k_e·f²·B²)·50
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(190px,45vw),1fr))", gap:"0 24px" }}>
                  <PI label={e("스위칭 에너지 E_on_off","Switching Energy E_on_off")}
                    value={(params.E_on_off??0.003)*1000}
                    onChange={v=>up("E_on_off",v/1000)}
                    unit="mJ" step={0.5} min={0.1} max={20}
                    desc={e("스위칭 1회당 에너지 손실 (SiC: 0.5~2mJ, IGBT: 2~8mJ)","Per-switch energy loss (SiC:0.5~2mJ, IGBT:2~8mJ)")} />
                  <PI label={e("다이오드 순방향 전압 V_f","Diode Forward Voltage V_f")}
                    value={params.V_f_diode??1.5}
                    onChange={v=>up("V_f_diode",v)}
                    unit="V" step={0.1} min={0.3} max={3.0}
                    desc={e("SiC SBD: 0.8~1.2V | Si 다이오드: 1.0~1.8V","SiC SBD:0.8~1.2V | Si diode:1.0~1.8V")} />
                  <PI label={e("MOSFET 온 저항 R_sw","MOSFET On-Resistance R_sw")}
                    value={(params.R_sw??0.002)*1000}
                    onChange={v=>up("R_sw",v/1000)}
                    unit="mΩ" step={0.5} min={0.1} max={20}
                    desc={e("SiC: 1~5mΩ | Si-IGBT: 3~10mΩ","SiC:1~5mΩ | Si-IGBT:3~10mΩ")} />
                  <PI label={e("철손 Steinmetz kh","Iron Loss Steinmetz kh")}
                    value={params.k_h??0.02}
                    onChange={v=>up("k_h",v)}
                    unit="" step={0.005} min={0.001} max={0.2}
                    desc={e("히스테리시스 손실 계수 (실측 또는 카탈로그)","Hysteresis loss coefficient")} />
                  <PI label={e("철손 Steinmetz ke","Iron Loss Steinmetz ke")}
                    value={params.k_e??0.0005}
                    onChange={v=>up("k_e",v)}
                    unit="" step={0.0001} min={0.00001} max={0.01}
                    desc={e("와전류 손실 계수","Eddy current loss coefficient")} />
                  <PI label={e("보조 전력 inv_aux_power","Inverter Aux Power")}
                    value={params.inv_aux_power??50}
                    onChange={v=>up("inv_aux_power",v)}
                    unit="W" step={5} min={0} max={500}
                    desc={e("팬·냉각·게이트드라이버 상시 소비 전력","Fan/cooling/gate-driver standby power")} />
                </div>
              </div>

            </div>
          )}


          {/* ══════════════════════════════════════════════════════════
              [A16] BMS 제어 흐름 블럭도
          ══════════════════════════════════════════════════════════ */}
          {group === "bms" && (() => {
            // ── 헬퍼 ──────────────────────────────────────────────────────
            const mkBlk = (blk, minW=82) => {
              const isActive = blk.tabs && blk.tabs.includes(subTab);
              return (
                <div key={blk.id}
                  onClick={() => blk.tabs && blk.tabs.length > 0 && setSubTab(blk.tabs[0])}
                  style={{ padding:"7px 9px", borderRadius:'6px', textAlign:"center", minWidth:minW,
                    background: isActive ? `${blk.color}28` : COLORS.bg,
                    border:`2px solid ${isActive ? blk.color : COLORS.border}`,
                    boxShadow: isActive ? `0 0 8px ${blk.color}55` : "none",
                    cursor: blk.tabs && blk.tabs.length > 0 ? "pointer" : "default",
                    transition:"all 0.2s" }}>
                  <div style={{fontSize:'14px',lineHeight:1,marginBottom:'2px'}}>{blk.icon}</div>
                  <div style={{fontSize:8.5,fontWeight:700,lineHeight:1.3,
                    color:isActive?blk.color:COLORS.text}}>{blk.label}</div>
                  {blk.val && <div style={{fontSize:9.5,fontWeight:800,color:blk.color,marginTop:'2px',fontFamily:"monospace"}}>{blk.val}</div>}
                  <div style={{fontSize:'7px',color:COLORS.textMuted,marginTop:'2px',
                    lineHeight:1.35,whiteSpace:"pre-line"}}>{blk.sub}</div>
                </div>
              );
            };
            const mkArr = (label="", color=COLORS.textMuted, bidir=false, vertical=false) => (
              <div style={{display:"flex",flexDirection:vertical?"row":"column",alignItems:"center",
                padding:"0 2px",flexShrink:0,justifyContent:"center"}}>
                <div style={{fontSize:'11px',color,lineHeight:1}}>{vertical?"↕":bidir?"↔":"→"}</div>
                {label && <div style={{fontSize:'6px',color,marginTop:'1px',textAlign:"center",
                  whiteSpace:"nowrap",maxWidth:56}}>{label}</div>}
              </div>
            );

            // ── 실시간 KPI ─────────────────────────────────────────────
            const S = params.bms_cell_count_s||96;
            const V_pack_nom = (3.7*S).toFixed(0)+"V";
            const soc_pct    = ((params.soc_init||0.9)*100).toFixed(0)+"%";
            const i_chg_max  = (params.bms_i_charge_max||200)+"A";
            const arb_kw     = (params.arb_chg_limit_kw||70)+"kW";

            // ── 상단 행: 측정→추정→제어→출력 ──────────────────────────
            const topRow = [
              { id:"sensor",    icon:"📡", color:"#38bdf8", tabs:[],
                label:e("센서 입력","Sensor Input"),
                val: `${S}S · T_amb=${params.T_amb||25}°C`,
                sub:e("V·I·T 셀별 측정","V·I·T Per-Cell") },
              { id:"adc",       icon:"🔢", color:"#818cf8", tabs:["bms_cell"],
                label:e("데이터 수집","Data Acq."),
                val: `${S*( params.bms_cell_count_p||4)}셀`,
                sub:e("A/D·MUX·필터","A/D·MUX·Filter") },
              { id:"state_soc", icon:"🧠", color:COLORS.accent3, tabs:["bms_soc"],
                label:"SOC/SoH/SoP",
                val: soc_pct,
                sub:e("기본 상태 추정","Basic State Est.") },
              { id:"state_kal", icon:"📡", color:'#f59e0b', tabs:["kalman_ext"],
                label:e("칼만 필터","Kalman Filter"),
                val: params.kalman_enable ? (params.kalman_type||"EKF") : "OFF",
                sub:e("고급 SOC 추정","Advanced SOC Est.") },
              { id:"ctrl",      icon:"🛡️", color:"#ef4444", tabs:["bms_volt"],
                label:e("보호·제어","Protection"),
                val: `${params.bms_v_cell_min||2.8}~${params.bms_v_cell_max||4.2}V`,
                sub:e("전압·전류·온도 한계","V·I·T Limits") },
              { id:"output",    icon:"🔌", color:"#f97316", tabs:["bms_output"],
                label:e("출력 제어","Output Ctrl"),
                val: i_chg_max,
                sub:e("릴레이·스위치·퓨즈","Relay·Switch·Fuse") },
              { id:"pack",      icon:"🔋", color:"#22c55e", tabs:[],
                label:e("배터리팩","Battery Pack"),
                val: V_pack_nom,
                sub:e("고전압 출력\n열관리","HV Output\nThermal Mgmt") },
            ];
            const topArrows = [
              {label:"ADC",c:"#818cf8"},{label:e("원시값","Raw"),c:COLORS.accent3},
              {label:"SOC",c:'#f59e0b'},{label:e("상태","State"),c:"#ef4444"},
              {label:e("명령","Cmd"),c:'#f59e0b'},{label:e("출력","Output"),c:"#f97316"},
            ];

            // ── 하단 행: 배터리 모델 / 중재 / 열관리 ─────────────────
            const botRow = [
              { id:"batt_model", icon:"⚡", color:COLORS.accent3, tabs:["bms_cell"],
                label:e("배터리 모델","Battery Model"),
                val: params.battery_model==="2RC" ? "2RC ECM" : "Simple R",
                sub:e("OCV·내부저항","OCV·R_int") },
              { id:"arb",        icon:"⚖️", color:"#ef4444",      tabs:["bms_soc"],
                label:e("중재","Arbitration"),
                val: arb_kw,
                sub:e("회생·구동 전력 제한","Regen·Drive Limit") },
              { id:"therm",      icon:"🔥", color:"#f97316",      tabs:["bms_soc"],
                label:e("열 모델","Thermal Model"),
                val: `C=${(params.C_th_batt||80000)/1000}kJ`,
                sub:"C_th·R_th·Derate" },
              { id:"sei_soh",    icon:"🧬", color:"#34d399",      tabs:["bms_cell"],
                label:"SEI / SoH",
                val: [params.sei_enable?"SEI":"", params.soh_enable?"SoH":""].filter(Boolean).join("·")||"OFF",
                sub:e("열화 추적","Degradation Tracking") },
              { id:"balance",    icon:"⚖️", color:'#f59e0b',      tabs:["bms_output"],
                label:e("밸런싱","Balancing"),
                val: params.bms_balance_strategy||"passive",
                sub:`ΔV>${(params.bms_balance_threshold||0.01)*1000}mV` },
            ];

            return (
              <div style={{ marginBottom:'14px', padding:"12px 14px", borderRadius:'8px',
                background:COLORS.panel, border:`1px solid ${COLORS.border}` }}>
                <div style={{fontSize:'10px',fontWeight:700,color:COLORS.accent3,marginBottom:'8px',display:"flex",alignItems:"center",gap:'8px'}}>
                  🔋 {e("BMS 제어 흐름 블럭도 — 탭 클릭으로 이동","BMS Control Flow — click block to navigate")}
                  <span style={{fontSize:'8px',color:COLORS.textMuted,fontWeight:400}}>
                    {e("상단: 신호 흐름 / 하단: 모델·중재·열화","Top: signal flow / Bottom: models·arbitration·degradation")}
                  </span>
                </div>

                {/* 상단 행 */}
                <div style={{display:"flex",alignItems:"center",overflowX:"auto",paddingBottom:'4px',gap:0,minWidth:"max-content",marginBottom:'4px'}}>
                  {topRow.map((blk,i) => (
                    <React.Fragment key={blk.id}>
                      {mkBlk(blk)}
                      {i < topRow.length-1 && mkArr(topArrows[i]?.label, topArrows[i]?.c)}
                    </React.Fragment>
                  ))}
                </div>

                {/* 수직 구분선 */}
                <div style={{borderTop:`1px dashed ${COLORS.border}`,margin:"6px 0",position:"relative"}}>
                  <span style={{position:"absolute",left:"50%",top:-8,transform:"translateX(-50%)",
                    fontSize:'7px',color:COLORS.textMuted,background:COLORS.panel,padding:"0 6px"}}>
                    {e("지원 모델 · 중재 · 열화","Supporting Models · Arbitration · Degradation")}
                  </span>
                </div>

                {/* 하단 행 */}
                <div style={{display:"flex",alignItems:"center",overflowX:"auto",gap:'6px',paddingBottom:'4px',minWidth:"max-content"}}>
                  {botRow.map(blk => mkBlk(blk, 90))}
                </div>

                {/* 피드백 배지 */}
                <div style={{marginTop:'8px',display:"flex",gap:'6px',flexWrap:"wrap"}}>
                  {[
                    {c:"#38bdf8", label:e("실시간 모니터링","Real-time Monitor"),
                      desc:e("배터리팩 → 센서 → 데이터 수집 (연속)","Pack → Sensor → Data Acq. (continuous)")},
                    {c:COLORS.accent3, label:e("상태→제어","State→Control"),
                      desc:e("SOC·SoH → 보호 로직 → 출력 결정","SOC·SoH → Protection → Output")},
                    {c:"#ef4444", label:e("즉시 차단","Immediate Cutoff"),
                      desc:e("보호 로직 → 릴레이·퓨즈 (폴트 래치)","Protection → Relay·Fuse (fault latch)")},
                    {c:'#f59e0b', label:e("중재 피드백","Arb Feedback"),
                      desc:e("SOC·온도 → 충방전 전력 한계 실시간 갱신","SOC·T → power limits updated in real time")},
                  ].map((fb,i) => (
                    <div key={i} style={{display:"flex",alignItems:"center",gap:'5px',
                      padding:"3px 10px",borderRadius:'4px',
                      background:`${fb.c}15`,border:`1px solid ${fb.c}40`}}>
                      <span style={{fontSize:'9px',color:fb.c}}>↺</span>
                      <span style={{fontSize:'9px',fontWeight:600,color:fb.c}}>{fb.label}</span>
                      <span style={{fontSize:7.5,color:COLORS.textMuted}}>{fb.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

    </div>
  );
}